const IMG = (id) => `https://images.unsplash.com/${id}?q=80&w=1920&auto=format&fit=crop`;
const APP = "https://matcalc360.pro";

export const PAGES = {
  "roofing-calculator": {
    slug: "roofing-calculator",
    projectType: "Roofing & Exteriors",
    seoTitle: "Free Roofing Calculator — Shingles, Squares & Bundles · No Login | MatCalc360",
    metaDescription:
      "Free roofing calculator — no login, no signup. Estimate roof squares, shingle bundles, underlayment and nails from your measurements. Metal, 3-tab, architectural and cedar modes with pitch and waste included.",
    kicker: "MC360 // Roofing & Exteriors",
    h1Lines: [
      [{ t: "ROOFING MATERIALS," }],
      [{ t: "CALCULATED ", accent: true }, { t: "TO THE" }],
      [{ t: "LAST BUNDLE." }],
    ],
    intro:
      "How many shingles do I need? Stop guessing. Enter the footprint, pitch and waste factor — the free roofing calculator turns them into squares, shingle bundles, underlayment rolls and nail counts you can hand straight to the supplier.",
    heroImage: IMG("photo-1646324554833-f0b6a479fa5d"),
    heroImageAlt: "Roofing contractor working on a residential job site at golden hour",
    primaryCta: { label: "Try the Roofing Calculator", href: `${APP}/?tab=roofing` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Roofing Calculator",
      path: "/?tab=roofing",
      inputs: [
        { label: "Roof Length", value: "42 ft" },
        { label: "Roof Width", value: "28 ft" },
        { label: "Roof Pitch", value: "6/12" },
        { label: "Waste Factor", value: "10 %" },
      ],
      outputs: [
        { label: "Roof Area", value: "1,318 sq ft" },
        { label: "Roof Squares", value: "13.2" },
        { label: "Shingle Bundles", value: "40", highlight: true },
        { label: "Underlayment", value: "7 rolls" },
      ],
    },
    guideTitle: "HOW A ROOF TAKEOFF ACTUALLY WORKS",
    chapters: [
      {
        heading: "Start with the footprint",
        body: "Every roofing estimate begins with area, and every roof plane is just a rectangle until proven otherwise. Measure each plane eave-to-ridge and rake-to-rake, or pull the building footprint and adjust for overhangs. Complex roofs — valleys, dormers, hips — get broken into simple planes and added together.",
        points: [
          "Measure each plane separately",
          "Break complex roofs into rectangles",
          "Dormers and valleys get their own planes",
        ],
      },
      {
        heading: "Pitch changes everything",
        body: "A 1,176 sq ft footprint is not 1,176 sq ft of roof. Slope stretches the surface, and the steeper the pitch, the more material it takes to cover the same building. Multiply the footprint by the pitch factor before you order anything.",
        points: ["4/12 pitch — multiply by 1.054", "6/12 pitch — multiply by 1.118", "8/12 pitch — multiply by 1.202"],
      },
      {
        heading: "Squares, bundles and the waste rule",
        body: "Roofing is sold in squares: one square covers 100 sq ft. Standard architectural shingles run three bundles per square. Then add waste — 10% for a simple gable, up to 15% for cut-up roofs with hips and valleys. The shingle calculator does the rounding up for you.",
        points: ["1 square = 100 sq ft of roof", "3 bundles per square (architectural)", "10–15% waste depending on complexity"],
      },
    ],
    visualImage: IMG("photo-1429497419816-9ca5cfb4571a"),
    visualImageAlt: "Crew working at height on a structure under construction",
    visualStat: { kicker: "FIELD ACCURACY", value: "±2%", label: "typical variance between a MatCalc360 takeoff and the final material order" },
    faqs: [
      {
        q: "What is a roofing square?",
        a: "A roofing square is the standard unit of the trade: exactly 100 square feet of roof surface. Shingles, underlayment and labor rates are all quoted per square, which is why the calculator reports your roof in squares first.",
      },
      {
        q: "How many bundles of shingles do I need per square?",
        a: "Most architectural (laminated) shingles come three bundles to a square. Traditional 3-tab shingles also run three per square, while some heavyweight designer shingles need four. Check the bundle coverage printed on your specific product.",
      },
      {
        q: "How does roof pitch affect my material order?",
        a: "Pitch increases the actual surface area over the same footprint. A 6/12 roof has about 12% more surface than its flat footprint, and a 12/12 roof has 41% more. If you skip the pitch multiplier, you will under-order every time.",
      },
      {
        q: "How much waste should I add for a roofing job?",
        a: "Add 10% for a straightforward gable roof and 12–15% for hip roofs, valleys, dormers or steep pitches where cuts multiply. Starter strips and ridge caps consume additional material that the waste factor absorbs.",
      },
      {
        q: "Can I measure my roof without climbing it?",
        a: "Yes. Measure the building's footprint from the ground, add the overhang, and apply the pitch multiplier for your roof's slope. The MatCalc360 roofing calculator also includes a homeowner AI-photo mode that estimates measurements from a picture of your house.",
      },
    ],
    relatedSlugs: ["concrete-calculator", "brick-calculator", "siding-calculator"],
    cardBlurb: "Roof squares, shingle bundles, underlayment and nails — metal, 3-tab, architectural and cedar.",
    ctaText: "Try the Roofing Calculator",
  },

  "concrete-calculator": {
    slug: "concrete-calculator",
    projectType: "Concrete & Masonry",
    seoTitle: "Free Concrete Calculator — Cubic Yards & Bags · No Login | MatCalc360",
    metaDescription:
      "Free concrete calculator — no login, no signup. Find out how much concrete you need: convert slab, footing and driveway measurements into cubic yards and 60 lb / 80 lb bag counts with overage built in.",
    kicker: "MC360 // Concrete & Masonry",
    h1Lines: [
      [{ t: "POUR WITH CONFIDENCE —" }],
      [{ t: "ORDER THE " }, { t: "EXACT", accent: true }],
      [{ t: "YARDAGE." }],
    ],
    intro:
      "How much concrete do I need? It is the question that decides whether the pour finishes in one shot. Enter your slab, footing or driveway dimensions and get cubic yards, bag counts and truckloads — with overage calculated before the truck is booked.",
    heroImage: IMG("photo-1574757987642-5755f0839101"),
    heroImageAlt: "Worker pouring and leveling wet concrete on a slab",
    primaryCta: { label: "Try the Concrete Calculator", href: `${APP}/?tab=concrete` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Concrete Calculator",
      path: "/?tab=concrete",
      inputs: [
        { label: "Slab Length", value: "24 ft" },
        { label: "Slab Width", value: "18 ft" },
        { label: "Thickness", value: "4 in" },
        { label: "Overage", value: "10 %" },
      ],
      outputs: [
        { label: "Volume", value: "5.87 cu yd", highlight: true },
        { label: "80-lb Bags", value: "264" },
        { label: "60-lb Bags", value: "352" },
        { label: "Ready-Mix", value: "1 truck" },
      ],
    },
    guideTitle: "CONCRETE MATH, FROM FORMS TO TRUCK",
    chapters: [
      {
        heading: "Volume is length × width × depth",
        body: "Concrete volume is simple multiplication with one trap: units. Slab thickness is measured in inches, everything else in feet. Convert thickness to feet, multiply the three dimensions for cubic feet, then divide by 27 to get cubic yards — the unit ready-mix is sold in.",
        points: ["Convert inches to feet first (4 in = 0.333 ft)", "Cubic feet ÷ 27 = cubic yards", "Footings and columns use the same formula"],
      },
      {
        heading: "Bags vs. ready-mix",
        body: "One cubic yard of concrete takes about 45 bags of 80-lb mix or 60 bags of 60-lb mix. Bagged concrete makes sense up to roughly half a yard; beyond that, a ready-mix truck is cheaper, faster and far more consistent. The calculator shows both so you can decide on the spot.",
        points: ["≈ 45 × 80-lb bags per cubic yard", "≈ 60 × 60-lb bags per cubic yard", "Ready-mix wins above ~0.5 cu yd"],
      },
      {
        heading: "The overage that saves the pour",
        body: "Subgrades are never perfectly level, forms flex, and spillage happens. Order 5–10% extra on slabs and up to 15% on footings dug into uneven ground. Running short means a cold joint or an emergency short-load fee — both cost more than the extra half yard.",
        points: ["5–10% overage on slabs", "Up to 15% on excavated footings", "Short loads trigger surge fees"],
      },
    ],
    visualImage: IMG("photo-1504307651254-35680f356dfd"),
    visualImageAlt: "Workers tying steel rebar ahead of a concrete pour",
    visualStat: { kicker: "ONE NUMBER THAT MATTERS", value: "27", label: "cubic feet in every cubic yard — the conversion behind every concrete order" },
    faqs: [
      {
        q: "How many cubic yards do I need for a 10×10 slab?",
        a: "At the standard 4-inch thickness, a 10×10 slab is 100 sq ft × 0.333 ft = 33.3 cubic feet, which divides out to 1.23 cubic yards. With 10% overage, order 1.4 yards — or roughly 56 bags of 80-lb mix.",
      },
      {
        q: "How many 80-lb bags of concrete make a cubic yard?",
        a: "About 45 bags. An 80-lb bag yields roughly 0.6 cubic feet of cured concrete, and a cubic yard is 27 cubic feet. For 60-lb bags the count rises to about 60 per yard.",
      },
      {
        q: "How thick should a concrete slab be?",
        a: "Four inches is standard for patios, sidewalks and shed floors. Driveways that carry vehicles should be 5–6 inches, and areas with heavy loads or poor soils may need 6 inches plus reinforcement.",
      },
      {
        q: "Should I order extra concrete?",
        a: "Always. Add 5–10% for level slabs and more for footings in uneven excavations. Leftover concrete can be formed into stepping stones or blocks; a short pour leaves a structural weak point you cannot fix cheaply.",
      },
      {
        q: "What is the difference between cubic feet and cubic yards?",
        a: "A cubic yard is 27 cubic feet — a cube three feet on each side. Bagged mixes are rated in cubic feet while ready-mix trucks sell in cubic yards, so every concrete estimate crosses this conversion at some point.",
      },
    ],
    relatedSlugs: ["asphalt-calculator", "stone-calculator", "brick-calculator"],
    cardBlurb: "Cubic yards and 60/80-lb bag counts for slabs, footings and driveways.",
    ctaText: "Try the Concrete Calculator",
  },

  "asphalt-calculator": {
    slug: "asphalt-calculator",
    projectType: "Paving & Sitework",
    seoTitle: "Free Asphalt Calculator — Tonnage & Cubic Yards · No Login | MatCalc360",
    metaDescription:
      "Free asphalt calculator — no login, no signup. Estimate hot-mix asphalt tonnage for driveways and parking lots. Enter area and compacted depth — get tons, cubic yards and truckloads instantly.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "PAVING TONNAGE," }],
      [{ t: "DOWN TO " }, { t: "THE LAST", accent: true }],
      [{ t: "TON." }],
    ],
    intro:
      "How many tons of asphalt do I need? Asphalt is bought by the ton and laid by the square foot — bridging that gap is where estimates go wrong. Enter your area and compacted depth, and get the tonnage, volume and truckloads your paver actually needs.",
    heroImage: IMG("photo-1465447142348-e9952c393450"),
    heroImageAlt: "Aerial view of a freshly paved highway interchange",
    primaryCta: { label: "Try the Asphalt Calculator", href: `${APP}/?tab=asphalt` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Asphalt Calculator",
      path: "/?tab=asphalt",
      inputs: [
        { label: "Area Length", value: "60 ft" },
        { label: "Area Width", value: "12 ft" },
        { label: "Compacted Depth", value: "3 in" },
        { label: "Mix Density", value: "145 lb/ft³" },
      ],
      outputs: [
        { label: "Coverage", value: "720 sq ft" },
        { label: "Volume", value: "6.67 cu yd" },
        { label: "Tonnage", value: "13.1 tons", highlight: true },
        { label: "Truckloads", value: "1" },
      ],
    },
    guideTitle: "HOW ASPHALT TONNAGE IS REALLY FIGURED",
    chapters: [
      {
        heading: "Tonnage is a density problem",
        body: "Hot-mix asphalt weighs about 145 pounds per cubic foot compacted. Multiply your area by depth to get cubic feet, multiply by density to get pounds, then divide by 2,000 for tons. The calculator keeps the density adjustable because binder and surface mixes differ slightly.",
        points: ["Standard density ≈ 145 lb/ft³", "Area × depth × density ÷ 2,000 = tons", "Adjust density for specialty mixes"],
      },
      {
        heading: "Compacted depth vs. loose depth",
        body: "Asphalt compacts roughly 20–25% under the roller. A 3-inch finished mat starts as about 3.75 inches of loose material. Estimates should always be based on the compacted depth — the number on the plan — because density ratings already assume compaction.",
        points: ["Figure on compacted (finished) depth", "Loose lay runs ~25% thicker", "Residential drives: 2.5–3 in compacted"],
      },
      {
        heading: "Order for the paver, not the spreadsheet",
        body: "Hot mix waits for no one: it has to be placed and rolled while it is still workable. Round tonnage up to the plant's minimum load, coordinate truck spacing with the crew, and never split a driveway into two pours a day apart if you can avoid it.",
        points: ["Round up to plant minimums", "Stage trucks to keep the paver moving", "One continuous pour beats two cold joints"],
      },
    ],
    visualImage: IMG("photo-1469854523086-cc02fe5d8800"),
    visualImageAlt: "Asphalt road cutting through open desert country",
    visualStat: { kicker: "ONE TON COVERS", value: "≈ 55 ft²", label: "of pavement at a 3-inch compacted depth — the number behind every tonnage order" },
    faqs: [
      {
        q: "How much does one ton of asphalt cover?",
        a: "At a 3-inch compacted depth, one ton of standard hot-mix covers roughly 55 square feet. At 2 inches it stretches to about 80 square feet, and at 4 inches it covers around 40 square feet.",
      },
      {
        q: "How many tons of asphalt does a typical driveway need?",
        a: "A single-car driveway around 12×50 ft at 3 inches compacted takes about 11 tons. Double-wide driveways of 20×50 ft run closer to 18 tons. Enter your exact dimensions in the asphalt calculator for a precise figure.",
      },
      {
        q: "Why is asphalt sold by the ton instead of by volume?",
        a: "Because weight is what the plant can measure accurately. Mix density varies with aggregate and temperature, so plants load trucks on scales and price by the ton. Your estimate converts area and depth into that same unit.",
      },
      {
        q: "How thick should a residential asphalt driveway be?",
        a: "Most residential driveways perform well at 2.5 to 3 inches of compacted hot-mix over a properly compacted gravel base. Heavy vehicles or soft soils justify 4 inches or a two-lift installation.",
      },
      {
        q: "Do I need a gravel base under asphalt?",
        a: "Yes. Asphalt is a flexible pavement and depends on its base for strength. A typical residential section is 6–8 inches of compacted crushed gravel beneath the asphalt mat — size it with the crushed stone calculator.",
      },
    ],
    relatedSlugs: ["concrete-calculator", "stone-calculator", "roofing-calculator"],
    cardBlurb: "Tons, cubic yards and truckloads for driveways, lots and walkways.",
    ctaText: "Try the Asphalt Calculator",
  },

  "drywall-calculator": {
    slug: "drywall-calculator",
    projectType: "Interiors & Drywall",
    seoTitle: "Free Drywall Calculator — Sheets, Mud, Tape & Screws · No Login | MatCalc360",
    metaDescription:
      "Free drywall calculator — no login, no signup. Estimate drywall sheets, joint compound, tape and screws for any room. Enter wall and ceiling dimensions — get 4×8 or 4×12 sheet counts with waste built in.",
    kicker: "MC360 // Interiors & Drywall",
    h1Lines: [
      [{ t: "DRYWALL MATH," }],
      [{ t: "WITHOUT " }, { t: "THE", accent: true }],
      [{ t: "GUESSWORK." }],
    ],
    intro:
      "How many sheets of drywall do I need? Every drywall job is four estimates wearing one coat — sheets, mud, tape, screws. Enter the room dimensions and ceiling height, and get a complete hanging-and-finishing list with cutting waste already counted.",
    heroImage: IMG("photo-1562259949-e8e7689d7828"),
    heroImageAlt: "Roller applying finish over freshly hung drywall",
    primaryCta: { label: "Try the Drywall Calculator", href: `${APP}/?tab=drywall` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Drywall Calculator",
      path: "/?tab=drywall",
      inputs: [
        { label: "Room Length", value: "16 ft" },
        { label: "Room Width", value: "12 ft" },
        { label: "Ceiling Height", value: "9 ft" },
        { label: "Sheet Size", value: "4 × 8 ft" },
      ],
      outputs: [
        { label: "Wall + Ceiling", value: "696 sq ft" },
        { label: "Sheets (4×8)", value: "24", highlight: true },
        { label: "Joint Compound", value: "≈ 2 buckets" },
        { label: "Screws", value: "≈ 750" },
      ],
    },
    guideTitle: "FROM ROOM DIMENSIONS TO A FINISHING LIST",
    chapters: [
      {
        heading: "Measure walls and ceiling separately",
        body: "Wall area is the room's perimeter times ceiling height; the ceiling is simply length times width. Add them for total coverage, then subtract only the largest openings — standard doors and windows are usually left in the estimate to absorb cutting waste.",
        points: ["Walls = perimeter × ceiling height", "Ceiling = length × width", "Standard openings offset cut waste"],
      },
      {
        heading: "Sheet size is a labor decision",
        body: "A 4×8 sheet covers 32 square feet; a 4×12 covers 48 and produces fewer butt joints — the hardest seams to finish. Longer sheets mean less taping and faster finishing, but they are heavier to hang and harder to get into the room. The sheetrock calculator prices both options.",
        points: ["4×8 sheet = 32 sq ft", "4×12 sheet = 48 sq ft, fewer seams", "Add ~10% sheets for cutting waste"],
      },
      {
        heading: "Mud, tape and screws scale with sheets",
        body: "Everything downstream follows the sheet count: roughly 32 screws per sheet, about one five-gallon bucket of joint compound per 500 square feet for three-coat finishing, and tape footage that tracks total seam length. Get the sheets right and the rest of the list writes itself.",
        points: ["≈ 32 screws per sheet (16″ OC)", "1 bucket mud ≈ 500 sq ft, 3 coats", "Tape ≈ one foot per 2.5–3 sq ft"],
      },
    ],
    visualImage: IMG("photo-1600607687939-ce8a6c25118c"),
    visualImageAlt: "Finished interior room with smooth drywall and natural light",
    visualStat: { kicker: "THE FINISHING CONSTANT", value: "32", label: "screws per sheet at standard 16-inch stud spacing" },
    faqs: [
      {
        q: "How many sheets of drywall do I need for a 12×12 room?",
        a: "With 8-foot ceilings, a 12×12 room has 384 sq ft of wall and 144 sq ft of ceiling — 528 sq ft total. That is about 17 sheets of 4×8, or 18 once you add cutting waste. With 4×12 sheets the count drops to about 12.",
      },
      {
        q: "Should I use 4×8 or 4×12 drywall sheets?",
        a: "Use 4×12 sheets whenever the room allows them: fewer butt joints means less taping, less mud and a flatter finish. Use 4×8 sheets for tight stairwells, small bathrooms or when you are hanging alone — they are half the weight per trip.",
      },
      {
        q: "How many screws go in a sheet of drywall?",
        a: "About 32 per sheet on walls with 16-inch stud spacing — screws every 8 inches along the edges and every 12 inches in the field. Ceilings use a slightly denser pattern. A one-pound box covers roughly 9–10 sheets.",
      },
      {
        q: "How much joint compound do I need?",
        a: "Plan on one five-gallon bucket per 500 square feet of drywall for a standard three-coat finish, plus one roll of 500-foot tape per 1,500–2,000 square feet. Buying one extra bucket beats a mid-Sunday run with wet seams waiting.",
      },
      {
        q: "Do bathrooms need special drywall?",
        a: "Yes. Use moisture-resistant board (green board) on bathroom walls outside the direct shower zone, and a cement backer board or fiber-mat gypsum panel inside tub and shower surrounds. The sheet count stays the same — only the product changes.",
      },
    ],
    relatedSlugs: ["paint-calculator", "siding-calculator", "roofing-calculator"],
    cardBlurb: "Sheets, mud, tape and screws for any room, walls and ceiling included.",
    ctaText: "Try the Drywall Calculator",
  },

  "brick-calculator": {
    slug: "brick-calculator",
    projectType: "Concrete & Masonry",
    seoTitle: "Free Brick Calculator — Bricks, Mortar & Block · No Login | MatCalc360",
    metaDescription:
      "Free brick calculator — no login, no signup. Find out how many bricks you need for any wall: brick counts per square foot, mortar bags and block (CMU) options with waste included.",
    kicker: "MC360 // Concrete & Masonry",
    h1Lines: [
      [{ t: "EVERY BRICK IN" }],
      [{ t: "THE WALL — " }, { t: "COUNTED", accent: true }],
      [{ t: "BEFORE YOU ORDER." }],
    ],
    intro:
      "How many bricks do I need? One number decides the whole masonry order. Enter wall dimensions and brick type, and the free brick calculator returns brick counts, mortar bags and a block alternative — waste factored in from the start.",
    heroImage: IMG("photo-1547056961-3c25e9140b05"),
    heroImageAlt: "Weathered red brick wall with traditional mortar joints",
    primaryCta: { label: "Try the Brick Calculator", href: `${APP}/?tab=brick` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Brick Calculator",
      path: "/?tab=brick",
      inputs: [
        { label: "Wall Length", value: "20 ft" },
        { label: "Wall Height", value: "8 ft" },
        { label: "Brick Type", value: "Modular" },
        { label: "Waste Factor", value: "7 %" },
      ],
      outputs: [
        { label: "Wall Area", value: "160 sq ft" },
        { label: "Bricks", value: "1,120", highlight: true },
        { label: "Mortar", value: "≈ 5 bags" },
        { label: "Block Option", value: "180 CMU" },
      ],
    },
    guideTitle: "BRICK MATH WITHOUT THE MASON'S NOTEBOOK",
    chapters: [
      {
        heading: "Bricks per square foot is the whole game",
        body: "A standard modular brick with a 3/8-inch mortar joint covers about 6.85 per square foot — most estimators round to 7. Multiply wall area by the rate for your brick size, because queen, king and utility bricks all cover differently and the count swings by thousands on a big wall.",
        points: ["Modular ≈ 7 bricks per sq ft", "Queen ≈ 5.8, King ≈ 5.3 per sq ft", "Joint thickness changes the rate"],
      },
      {
        heading: "Mortar follows the brick count",
        body: "Figure roughly three 70-lb bags of mortar per 100 bricks for typical joint sizes — about 30 bags per 1,000 bricks. Pre-blended Type N covers most above-grade residential work; Type S steps in for below-grade and structural applications.",
        points: ["≈ 3 bags mortar per 100 bricks", "Type N: general above-grade walls", "Type S: foundations and retaining"],
      },
      {
        heading: "Waste, breakage and the block option",
        body: "Add 5–10% for cuts, breakage and the inevitable dropped pallet corner. If the schedule matters more than the look, the same wall in 8×8×16 CMU block takes about 1.125 blocks per square foot — the calculator shows both counts side by side.",
        points: ["5–10% waste on brick", "≈ 1.125 CMU blocks per sq ft", "Corners and openings add cuts"],
      },
    ],
    visualImage: IMG("photo-1585750629386-33bdc1431574"),
    visualImageAlt: "Clean red brick wall laid in running bond pattern",
    visualStat: { kicker: "THE MASON'S CONSTANT", value: "7", label: "modular bricks per square foot of wall — the rate behind every brick order" },
    faqs: [
      {
        q: "How many bricks do I need per square foot?",
        a: "About 7 modular bricks per square foot with standard 3/8-inch mortar joints. Larger formats need fewer: queen size runs about 5.8 and king size about 5.3 per square foot. Always confirm the rate for your specific brick.",
      },
      {
        q: "How many bricks do I need for a 100 sq ft wall?",
        a: "Roughly 700 modular bricks before waste, or about 750 with a 7% waste allowance. The same wall in 8-inch CMU block takes about 113 blocks. Enter your exact dimensions in the brick calculator for a precise count.",
      },
      {
        q: "How much mortar do I need for 1,000 bricks?",
        a: "Plan on about 30 bags of 70-lb pre-mixed mortar — roughly three bags per 100 bricks at typical joint thickness. Deep joints, raked profiles and buttering habits push consumption higher, so buying an extra pallet strap of bags is cheap insurance.",
      },
      {
        q: "What is the difference between brick and CMU block?",
        a: "Brick is a facing material — smaller units, laid for appearance. CMU (concrete masonry unit) block is a structural wall system: each 8×8×16 block covers about 0.89 sq ft, so walls go up roughly seven times faster than brick. Many walls are block behind, brick in front.",
      },
      {
        q: "How much waste should I add to a brick order?",
        a: "Add 5% for straight walls with few openings and 7–10% for walls with many windows, corners or soldier courses. Brick also breaks in transit and on the saw — a spare half cube on site is normal practice, not overspending.",
      },
    ],
    relatedSlugs: ["stone-calculator", "concrete-calculator", "roofing-calculator"],
    cardBlurb: "Brick counts, mortar bags and block alternatives for any wall.",
    ctaText: "Try the Brick Calculator",
  },

  "stone-calculator": {
    slug: "stone-calculator",
    projectType: "Paving & Sitework",
    seoTitle: "Free Crushed Stone Calculator — Tons & Cubic Yards · No Login | MatCalc360",
    metaDescription:
      "Free crushed stone calculator — no login, no signup. Estimate ABC, crusher run and gravel tonnage for driveways and bases. Enter area and depth — get tons and cubic yards with compaction factored in.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "BASE STONE BY" }],
      [{ t: "THE " }, { t: "TON", accent: true }, { t: " — NOT BY" }],
      [{ t: "THE GUESS." }],
    ],
    intro:
      "How much crushed stone do I need? Whether it is ABC, crusher run or #57 gravel, the order is placed in tons and the hole is measured in feet. Enter area and depth — the stone calculator converts it into cubic yards and tons with compaction already handled.",
    heroImage: IMG("photo-1620914695139-9507be60a425"),
    heroImageAlt: "Pile of crushed stone aggregate at a quarry",
    primaryCta: { label: "Try the Stone Calculator", href: `${APP}/?tab=stone` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Stone Calculator",
      path: "/?tab=stone",
      inputs: [
        { label: "Area Length", value: "40 ft" },
        { label: "Area Width", value: "12 ft" },
        { label: "Depth", value: "4 in" },
        { label: "Material", value: "ABC / Crusher Run" },
      ],
      outputs: [
        { label: "Coverage", value: "480 sq ft" },
        { label: "Volume", value: "5.9 cu yd" },
        { label: "Tonnage", value: "8.0 tons", highlight: true },
        { label: "Compaction", value: "+10 %" },
      ],
    },
    guideTitle: "CRUSHED STONE, FROM AREA TO AXLE WEIGHT",
    chapters: [
      {
        heading: "Yards first, then tons",
        body: "Stone starts as volume: area times depth gives cubic feet, divided by 27 gives cubic yards. The quarry sells by weight, so multiply by the material's density — crushed stone runs about 1.3 to 1.5 tons per cubic yard depending on rock type and moisture.",
        points: ["Area × depth ÷ 27 = cubic yards", "≈ 1.35 tons per cu yd (typical)", "Wet stone weighs more — order by volume mind-set"],
      },
      {
        heading: "Compaction is not optional",
        body: "Loose stone compacts 10–15% under the plate compactor or roller. If the plan calls for a 4-inch compacted base, you are really placing closer to 4.5 inches of loose material. The calculator adds the compaction factor so the finished depth lands where the plan says.",
        points: ["Add 10–15% for compaction", "Compact in 3–4 inch lifts", "Driveway bases: 4–6 in compacted"],
      },
      {
        heading: "ABC, crusher run or #57?",
        body: "ABC (aggregate base course) and crusher run contain fines that lock together under compaction — the right choice under driveways and slabs. Clean #57 stone has no fines and drains freely, making it the pick for drainage beds and behind retaining walls. Same math, different density.",
        points: ["ABC / crusher run: structural bases", "#57 clean stone: drainage layers", "Density differs — set the material"],
      },
    ],
    visualImage: IMG("photo-1531134428421-397218356f00"),
    visualImageAlt: "Close texture of dark crushed gravel aggregate",
    visualStat: { kicker: "QUARRY MATH", value: "1.35", label: "tons per cubic yard of crushed stone — the density behind every gravel order" },
    faqs: [
      {
        q: "How many tons are in a cubic yard of crushed stone?",
        a: "About 1.3 to 1.5 tons, with 1.35 tons per cubic yard as a solid estimating average for ABC and crusher run. Moisture, rock type and gradation shift the number, which is why quarries sell by the scale ticket, not the bucket.",
      },
      {
        q: "How much area does a ton of gravel cover?",
        a: "One ton covers roughly 110 square feet at 2 inches deep, about 55 square feet at 4 inches, and around 37 square feet at 6 inches. Enter your exact depth in the stone calculator — depth is where most gravel estimates go wrong.",
      },
      {
        q: "What is the difference between ABC stone and crusher run?",
        a: "They are functionally the same family: crushed rock blended with fines that compact into a hard, stable base. Names vary by region — ABC, crusher run, road base, DGA — but all are graded-to-fines materials for structural layers under pavement and slabs.",
      },
      {
        q: "How deep should a gravel driveway base be?",
        a: "Four to six inches of compacted ABC or crusher run handles typical passenger vehicles on decent soil. Soft clay, heavy trucks or freeze-thaw country justify 8 inches or more, placed and compacted in lifts no thicker than 4 inches.",
      },
      {
        q: "Should I add extra stone for compaction?",
        a: "Yes — 10 to 15 percent. Loose stone loses volume as it compacts, and edges always spread wider than the string line. Ordering short means a second delivery charge that usually exceeds the cost of the extra two tons.",
      },
    ],
    relatedSlugs: ["asphalt-calculator", "concrete-calculator", "brick-calculator"],
    cardBlurb: "ABC, crusher run and gravel tonnage for driveways, bases and drainage.",
    ctaText: "Try the Stone Calculator",
  },

  "board-feet-calculator": {
    slug: "board-feet-calculator",
    projectType: "Wood & Lumber",
    seoTitle: "Free Board Feet Calculator — Lumber Calculator · No Login | MatCalc360",
    metaDescription:
      "Free board feet calculator — no login, no signup. Calculate board feet for any lumber order: thickness × width × length, per piece or per thousand. Works for rough and dimensional lumber.",
    kicker: "MC360 // Wood & Lumber",
    h1Lines: [
      [{ t: "LUMBER BY THE" }],
      [{ t: "BOARD FOOT", accent: true }, { t: "," }],
      [{ t: "NOT THE GUESS." }],
    ],
    intro:
      "How do you calculate board feet? Thickness times width times length, divided by 12 — simple until you are pricing forty pieces of mixed hardwood at the yard. Enter dimensions and quantity, and get board feet per piece and per order instantly.",
    heroImage: IMG("photo-1589939705384-5185137a7f0f"),
    heroImageAlt: "Carpenter working with lumber beams and hand tools",
    primaryCta: { label: "Try the Board Feet Calculator", href: `${APP}/?tab=boardfeet` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Board Feet Calculator",
      path: "/?tab=boardfeet",
      inputs: [
        { label: "Thickness", value: "2 in" },
        { label: "Width", value: "4 in" },
        { label: "Length", value: "8 ft" },
        { label: "Quantity", value: "40 pcs" },
      ],
      outputs: [
        { label: "Per Piece", value: "5.33 BF" },
        { label: "Total", value: "213 BF", highlight: true },
        { label: "Nominal Size", value: "2×4×8" },
        { label: "Linear Feet", value: "320" },
      ],
    },
    guideTitle: "BOARD FEET: THE LUMBER INDUSTRY'S CURRENCY",
    chapters: [
      {
        heading: "The formula in one line",
        body: "One board foot is a piece of wood one inch thick, twelve inches wide and one foot long. The formula: thickness (inches) × width (inches) × length (feet) ÷ 12. A 2×4 eight feet long is 2 × 4 × 8 ÷ 12 = 5.33 board feet — regardless of what the yard calls it.",
        points: ["BF = T(in) × W(in) × L(ft) ÷ 12", "2×4×8 = 5.33 board feet", "1×12×8 = 8 board feet"],
      },
      {
        heading: "Nominal vs. actual — the hardwood trap",
        body: "Dimensional softwood sells by the piece using nominal names (a 2×4 is really 1.5×3.5). Rough hardwood sells by the true board foot using actual quarter-inch thickness — 4/4, 5/4, 8/4. Mixing the two systems is the most expensive mistake at the lumber counter.",
        points: ["Softwood: nominal names, priced per piece", "Hardwood: actual size, priced per BF", "4/4 = 1 in, 8/4 = 2 in thick"],
      },
      {
        heading: "Price per thousand",
        body: "Mills and hardwood dealers quote MFBM — price per thousand board feet. Multiply your total BF by the rate and divide by 1,000 for the order cost. The calculator keeps piece count, total board feet and cost in one view so quotes compare apples to apples.",
        points: ["MBF = thousand board feet", "Cost = total BF × rate ÷ 1,000", "Compare species by BF, not by piece"],
      },
    ],
    visualImage: IMG("photo-1656646549633-80ad4bd2ab40"),
    visualImageAlt: "Stacked lumber boards on an outdoor construction project",
    visualStat: { kicker: "THE YARDSTICK", value: "5.33", label: "board feet in every 2×4×8 — the conversion behind every lumber quote" },
    faqs: [
      {
        q: "How do you calculate board feet?",
        a: "Multiply thickness in inches by width in inches by length in feet, then divide by 12. For example, a board 2 inches thick, 6 inches wide and 10 feet long is 2 × 6 × 10 ÷ 12 = 10 board feet. The board feet calculator does this for whole lists of mixed sizes.",
      },
      {
        q: "How many board feet are in a 2×4×8?",
        a: "Using nominal dimensions, 5.33 board feet (2 × 4 × 8 ÷ 12). Using the actual 1.5 × 3.5-inch milled size, it is 3.5 board feet. Yards quote by nominal measure for dimensional lumber, so estimate the same way.",
      },
      {
        q: "What is the difference between board feet and linear feet?",
        a: "Linear feet measures only length — a 10-foot board is 10 linear feet regardless of size. Board feet measures volume, so a 1×12 holds twelve times the wood of a 1×1 at the same length. Trim and molding sell by the linear foot; lumber and hardwood sell by the board foot.",
      },
      {
        q: "Why is hardwood sold by the board foot?",
        a: "Because hardwood boards come in random widths and lengths — no two pieces are identical. Pricing by volume (board feet) is the only fair way to charge for irregular stock, unlike dimensional softwood where every 2×4×8 is the same piece.",
      },
      {
        q: "How many board feet do I need for a deck or floor?",
        a: "Multiply the square footage by the board thickness in inches: 100 sq ft of 5/4 decking (1.25 in) takes about 125 board feet before waste. Add 10–15% for cutting, and use actual milled dimensions for accuracy on big orders.",
      },
    ],
    relatedSlugs: ["roofing-calculator", "storage-building-calculator", "drywall-calculator"],
    cardBlurb: "Board feet per piece or per thousand for any lumber list, rough or dimensional.",
    ctaText: "Try the Board Feet Calculator",
  },

  "siding-calculator": {
    slug: "siding-calculator",
    projectType: "Roofing & Exteriors",
    seoTitle: "Free Vinyl Siding Calculator — Squares & Trim · No Login | MatCalc360",
    metaDescription:
      "Free vinyl siding calculator — no login, no signup. Find out how much siding you need: wall squares, gables, openings and trim linear feet with waste built in.",
    kicker: "MC360 // Roofing & Exteriors",
    h1Lines: [
      [{ t: "SIDE THE HOUSE" }],
      [{ t: "WITH " }, { t: "EXACT", accent: true }],
      [{ t: "SQUARES." }],
    ],
    intro:
      "How much siding do I need? Like roofing, vinyl siding sells by the square — 100 square feet per box count. Enter wall dimensions, subtract openings, add gables and waste, and walk into the supplier with squares and trim footage already figured.",
    heroImage: IMG("photo-1568605114967-8130f3a36994"),
    heroImageAlt: "House with wood siding exterior at dusk",
    primaryCta: { label: "Try the Siding Calculator", href: `${APP}/?tab=siding` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Vinyl Siding Calculator",
      path: "/?tab=siding",
      inputs: [
        { label: "House Length", value: "40 ft" },
        { label: "House Width", value: "30 ft" },
        { label: "Wall Height", value: "9 ft" },
        { label: "Openings", value: "210 sq ft" },
      ],
      outputs: [
        { label: "Net Wall Area", value: "1,050 sq ft" },
        { label: "Squares to Order", value: "12", highlight: true },
        { label: "Waste Allowance", value: "10 %" },
        { label: "Trim (J-Channel)", value: "≈ 140 LF" },
      ],
    },
    guideTitle: "SIDING SQUARES, GABLES AND TRIM — FIGURED",
    chapters: [
      {
        heading: "Walls are perimeter × height",
        body: "Measure around the house and multiply by wall height for gross wall area, then subtract window and door openings. Siding is sold in squares of 100 square feet, typically two squares per box. The math is friendly — the openings list is where estimates usually slip.",
        points: ["Gross area = perimeter × height", "Subtract windows and doors", "1 square = 100 sq ft of siding"],
      },
      {
        heading: "Gables are triangles — don't skip them",
        body: "Each gable end adds half its base times its height. A 24-foot gable with a 6-foot rise adds 72 square feet per end. Two-story and cross-gabled homes stack several — measure each one, because gables routinely hide 15% of a siding order.",
        points: ["Gable area = base × height ÷ 2", "Count every gable end separately", "Dormers count as small gables"],
      },
      {
        heading: "Trim is a second order of its own",
        body: "J-channel frames every window, door and wall top; starter strip runs the full perimeter; inside and outside corners run full height. Add 10% waste to the siding squares and list trim pieces in linear feet — the calculator keeps both on one sheet.",
        points: ["J-channel: around every opening", "Starter strip = house perimeter", "10% waste on squares"],
      },
    ],
    visualImage: IMG("photo-1600585154340-be6161a56a0c"),
    visualImageAlt: "Modern home exterior with finished siding at dusk",
    visualStat: { kicker: "SELLING UNIT", value: "100 ft²", label: "per square of siding — the unit every supplier quote is built on" },
    faqs: [
      {
        q: "How do I calculate how much siding I need?",
        a: "Multiply your house perimeter by wall height, subtract the area of windows and doors, add each gable (base × height ÷ 2), then add 10% for waste. Divide by 100 to get squares — the unit siding is sold in. The vinyl siding calculator does each step for you.",
      },
      {
        q: "What is a square of siding?",
        a: "A square is 100 square feet of siding coverage — the same selling unit roofing uses. Most vinyl siding ships two squares per box, so a 12-square order is six boxes of panels plus trim.",
      },
      {
        q: "Should I subtract windows and doors from siding?",
        a: "Yes — openings are the one place subtraction pays off, because siding is expensive per square foot. Subtract standard windows and doors fully, but keep the 10% waste factor afterward; cutoffs around those same openings are where the waste goes.",
      },
      {
        q: "How much waste should I add for vinyl siding?",
        a: "Ten percent covers most rectangular homes. Bump to 12–15% for walls with many angles, bump-outs or diagonal gable cuts. Panels come in fixed 12-foot lengths, so every seam produces a cutoff that may or may not be reusable.",
      },
      {
        q: "How much trim do I need with new siding?",
        a: "Figure J-channel around every window and door and along every wall top, starter strip equal to the full house perimeter, and corner posts at full wall height for every inside and outside corner. On a typical home, trim runs 10–15% of the total material budget.",
      },
    ],
    relatedSlugs: ["roofing-calculator", "paint-calculator", "brick-calculator"],
    cardBlurb: "Wall squares, gables and trim linear feet for vinyl siding jobs.",
    ctaText: "Try the Siding Calculator",
  },

  "paint-calculator": {
    slug: "paint-calculator",
    projectType: "Interiors & Paint",
    seoTitle: "Free Paint Calculator — Gallons Per Room · No Login | MatCalc360",
    metaDescription:
      "Free paint calculator — no login, no signup. Find out how much paint you need: gallons per room with coats, primer and ceiling figured separately. Covers walls, trim and ceilings.",
    kicker: "MC360 // Interiors & Paint",
    h1Lines: [
      [{ t: "HOW MUCH PAINT?" }],
      [{ t: "DOWN TO THE", accent: true }],
      [{ t: "LAST GALLON." }],
    ],
    intro:
      "How much paint do I need for a room? One gallon covers about 350 square feet — but coats multiply, primer is separate and ceilings are their own line item. Enter the room dimensions and get gallons for walls, ceiling and primer in one pass.",
    heroImage: IMG("photo-1525909002-1b05e0c869d8"),
    heroImageAlt: "Paint rollers applying bold color stripes to a wall",
    primaryCta: { label: "Try the Paint Calculator", href: `${APP}/?tab=paint` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Paint Calculator",
      path: "/?tab=paint",
      inputs: [
        { label: "Room Length", value: "16 ft" },
        { label: "Room Width", value: "12 ft" },
        { label: "Ceiling Height", value: "9 ft" },
        { label: "Coats", value: "2" },
      ],
      outputs: [
        { label: "Wall Area", value: "504 sq ft" },
        { label: "Wall Paint", value: "3 gal", highlight: true },
        { label: "Primer", value: "2 gal" },
        { label: "Ceiling Paint", value: "1 gal" },
      ],
    },
    guideTitle: "GALLONS, COATS AND COVERAGE — WITHOUT LEFTOVERS",
    chapters: [
      {
        heading: "Coverage starts at 350 square feet",
        body: "One gallon of quality interior paint covers 350–400 square feet per coat on smooth drywall. Wall area is perimeter times height minus doors and windows — typically 40 square feet of openings per room. Divide adjusted area by coverage and you have gallons per coat.",
        points: ["1 gal ≈ 350–400 sq ft per coat", "Walls = perimeter × height − openings", "Textured walls cut coverage ~20%"],
      },
      {
        heading: "Coats multiply — primer is separate",
        body: "Two coats is the honest default for color changes. New drywall or dark-to-light transitions need a dedicated primer coat first — primer covers less per gallon (300 sq ft) but seals the surface so the finish coats actually hit their coverage rating.",
        points: ["2 coats for color changes", "Primer: new drywall & drastic changes", "Primer ≈ 300 sq ft per gallon"],
      },
      {
        heading: "Ceilings and trim are their own list",
        body: "Ceiling paint is a different product — flat, spatter-resistant, usually white — figured from length times width. Trim paint covers baseboards, casings and doors; a gallon handles the trim of several average rooms. Round up, never down: one sealed leftover gallon beats one emergency quart.",
        points: ["Ceiling = length × width, flat white", "1 gal trim paint ≈ 3–4 rooms", "Round up to full gallons"],
      },
    ],
    visualImage: IMG("photo-1572627614522-1c56af1d9d72"),
    visualImageAlt: "Paint rollers and tray in dramatic dark light",
    visualStat: { kicker: "THE COVERAGE RULE", value: "350", label: "square feet per gallon per coat — the number behind every paint estimate" },
    faqs: [
      {
        q: "How much paint do I need for a 12×12 room?",
        a: "A 12×12 room with 8-foot ceilings has about 384 sq ft of wall. Two coats at 350 sq ft per gallon takes roughly 2.2 gallons — buy 3, or 2 gallons plus a quart. Add one gallon of primer for new drywall and a separate gallon for the ceiling.",
      },
      {
        q: "How many square feet does a gallon of paint cover?",
        a: "Between 350 and 400 square feet per coat on smooth, primed surfaces. Textured walls, unprimed drywall and porous masonry can drop real-world coverage to 250–300 square feet. The paint calculator lets you adjust coverage for surface condition.",
      },
      {
        q: "Do I need primer before painting?",
        a: "Primer is required on new drywall, bare wood, patched areas and stains, and strongly advised for dark-to-light color changes. It costs less than finish paint and prevents the topcoat from soaking in unevenly — skipping it usually means buying a third coat of the expensive stuff.",
      },
      {
        q: "How many coats of paint does a room need?",
        a: "Two coats over a similar existing color or primer. Drastic color changes — deep red to off-white, for example — can take primer plus two or even three finish coats to reach true color. Quality paint covers better per coat than bargain paint.",
      },
      {
        q: "Should I buy extra paint for touch-ups?",
        a: "Yes — keep one sealed gallon (or at minimum a quart) of every color. Walls get scuffed, and the same paint from the same batch blends invisibly; a color-matched can mixed two years later often flashes under side light.",
      },
    ],
    relatedSlugs: ["drywall-calculator", "siding-calculator", "unit-converter"],
    cardBlurb: "Gallons per room with coats, primer and ceilings figured separately.",
    ctaText: "Try the Paint Calculator",
  },

  "storage-building-calculator": {
    slug: "storage-building-calculator",
    projectType: "Wood & Lumber",
    seoTitle: "Free Shed Material Calculator — Storage Building Takeoff · No Login | MatCalc360",
    metaDescription:
      "Free shed material calculator — no login, no signup. Get a full storage building material list: framing studs, sheathing sheets, roofing squares and estimated cost for any shed size.",
    kicker: "MC360 // Wood & Lumber",
    h1Lines: [
      [{ t: "THE WHOLE SHED," }],
      [{ t: "ONE ", accent: true }, { t: "MATERIAL LIST." }],
    ],
    intro:
      "How much lumber do I need for a shed? A storage building is a hundred small decisions that all land on one purchase order. Enter length, width and wall height — the materials calculator returns the framing, sheathing, roofing and estimated cost as a single takeoff.",
    heroImage: IMG("photo-1518780664697-55e3ad937233"),
    heroImageAlt: "Small red storage shed standing on an open hill",
    primaryCta: { label: "Try the Materials Calculator", href: `${APP}/?tab=materials` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Materials Calculator",
      path: "/?tab=materials",
      inputs: [
        { label: "Building Length", value: "16 ft" },
        { label: "Building Width", value: "12 ft" },
        { label: "Wall Height", value: "8 ft" },
        { label: "Roof Style", value: "Gable" },
      ],
      outputs: [
        { label: "Wall Studs", value: "≈ 68" },
        { label: "Sheathing", value: "21 sheets" },
        { label: "Roofing", value: "2.2 squares" },
        { label: "Est. Materials", value: "$3,250", highlight: true },
      ],
    },
    guideTitle: "FROM SHED DIMENSIONS TO A FULL TAKEOFF",
    chapters: [
      {
        heading: "Framing counts follow the perimeter",
        body: "Wall studs at 16 inches on center mean roughly one stud per linear foot of wall, plus corners, door and window headers, and double top plates. A 12×16 shed has 56 feet of wall — the stud count lands near 70 once every plate, cripple and trimmer is honestly counted.",
        points: ["≈ 1 stud per LF of wall @16″ OC", "Corners, headers, plates add 20%+", "Floor joists mirror the same spacing"],
      },
      {
        heading: "Sheathing is area ÷ 32",
        body: "Wall and roof sheathing comes in 4×8 sheets — 32 square feet each. Wall area divided by 32 gives the sheet count, then the roof adds its own planes. OSB for the budget build, plywood where moisture lurks. The calculator lists walls, roof and floor separately.",
        points: ["1 sheet = 32 sq ft of coverage", "Walls + roof + floor counted apart", "OSB vs. plywood per exposure"],
      },
      {
        heading: "Roofing and the cost roll-up",
        body: "A shed roof is a small roofing job: squares of shingles, underlayment, drip edge and felt, all computed from the same pitch math as a house. Every line rolls into an estimated material cost so the build-vs-buy decision happens with real numbers, not a hunch.",
        points: ["Roof = same squares math as a house", "Underlayment and drip edge included", "Full cost estimate per takeoff"],
      },
    ],
    visualImage: IMG("photo-1587293852726-70cdb56c2866"),
    visualImageAlt: "Stacked building materials in a supply warehouse",
    visualStat: { kicker: "ONE SHEET COVERS", value: "32 ft²", label: "of wall or roof — the unit every sheathing count divides by" },
    faqs: [
      {
        q: "How much lumber do I need for a 12×16 shed?",
        a: "A 12×16 shed with 8-foot walls takes roughly 68 wall studs, 21 sheets of wall sheathing, floor joists and decking for 192 sq ft, and about 2.2 squares of roofing. The materials calculator generates the full line-item list with an estimated cost.",
      },
      {
        q: "How many studs do I need for a shed wall?",
        a: "Figure one stud per linear foot of wall at 16-inch spacing, then add 20% for corners, door and window framing, and doubled top plates. A 56-foot shed perimeter lands around 68 studs once the extras are counted honestly.",
      },
      {
        q: "How many sheets of OSB do I need for a shed?",
        a: "Divide total sheathing area by 32 (one 4×8 sheet). A 12×16 shed with 8-foot walls needs about 14 sheets for walls, 6 for the floor and 7–8 for a gable roof — around 27 sheets total. Buy two extra; sheets get damaged.",
      },
      {
        q: "How much does it cost to build a storage shed?",
        a: "A 12×16 DIY shed typically runs $2,500–$4,500 in materials depending on siding, roofing and window choices — versus $6,000+ for a comparable prebuilt delivered unit. Prices vary by region and lumber market; the calculator's cost estimate uses adjustable unit prices.",
      },
      {
        q: "What size shed can I build without a permit?",
        a: "Most jurisdictions exempt small accessory buildings between 100 and 200 square feet, but the threshold, setback rules and foundation requirements vary widely by county and HOA. Check your local building department before pouring anything — the material list works either way.",
      },
    ],
    relatedSlugs: ["board-feet-calculator", "roofing-calculator", "concrete-calculator"],
    cardBlurb: "Full framing, sheathing, roofing and cost takeoff for sheds and storage buildings.",
    ctaText: "Try the Materials Calculator",
  },

  "unit-converter": {
    slug: "unit-converter",
    projectType: "Tools & Converters",
    seoTitle: "Free Unit Converter — Length, Area, Volume & Weight · No Login | MatCalc360",
    metaDescription:
      "Free unit converter — no login, no signup. Convert length, area, volume, weight, temperature, energy, pressure and speed. Built for construction: feet-inches, cubic yards, square feet and more.",
    kicker: "MC360 // Tools & Converters",
    h1Lines: [
      [{ t: "EVERY UNIT." }],
      [{ t: "EVERY CONVERSION.", accent: true }],
      [{ t: "ZERO SIGNUP." }],
    ],
    intro:
      "Feet to meters, cubic yards to cubic meters, pounds to kilograms — the unit converter handles the eight categories construction math actually uses, with feet-inch friendliness built in. Free, instant, no login.",
    heroImage: IMG("photo-1581092160562-40aa08e78837"),
    heroImageAlt: "Engineer drafting precise measurements with calipers",
    primaryCta: { label: "Try the Unit Converter", href: `${APP}/?tab=convert` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Unit Converter",
      path: "/?tab=convert",
      inputs: [
        { label: "Value", value: "12.5" },
        { label: "From", value: "Feet" },
        { label: "To", value: "Meters" },
        { label: "Category", value: "Length" },
      ],
      outputs: [
        { label: "Result", value: "3.81 m", highlight: true },
        { label: "In Inches", value: "150 in" },
        { label: "In Yards", value: "4.167 yd" },
        { label: "Categories", value: "8 total" },
      ],
    },
    guideTitle: "THE CONVERSIONS CONSTRUCTION ACTUALLY USES",
    chapters: [
      {
        heading: "Length: the daily dozen",
        body: "Feet to meters for the imported spec sheet, inches to millimeters for the hardware order, feet-and-inches handled as a native format instead of an awkward decimal. One foot is exactly 0.3048 meters — the converter keeps full precision and rounds only for display.",
        points: ["1 ft = 0.3048 m exactly", "Feet-inch input supported", "Miles, yards, mm, cm included"],
      },
      {
        heading: "Area and volume: where estimates live",
        body: "Square feet to square meters for floor plans, cubic yards to cubic meters for concrete specs, and the acres conversion every lot measurement ends up needing. These are the conversions behind material orders, so the converter sits one tap away from every calculator.",
        points: ["1 sq ft = 0.0929 m²", "1 cu yd = 0.7646 m³", "1 acre = 43,560 sq ft"],
      },
      {
        heading: "Weight, temperature and the rest",
        body: "Pounds to kilograms for material densities, Fahrenheit to Celsius for curing windows and asphalt temperatures, plus energy, pressure and speed for equipment specs. Eight categories, one screen, no account wall between you and the answer.",
        points: ["1 lb = 0.4536 kg", "°F ↔ °C for cure temps", "Energy, pressure, speed built in"],
      },
    ],
    visualImage: IMG("photo-1504148455328-c376907d081c"),
    visualImageAlt: "Tape measure and drill on a plywood subfloor",
    visualStat: { kicker: "EXACT BY DEFINITION", value: "0.3048", label: "meters in every foot — the constant behind every length conversion" },
    faqs: [
      {
        q: "How do I convert feet to meters?",
        a: "Multiply feet by 0.3048. Ten feet is 3.048 meters. Going the other way, multiply meters by 3.2808. The converter handles both directions instantly, including feet-and-inches input like 12′ 6″.",
      },
      {
        q: "How do I convert square feet to square meters?",
        a: "Multiply square feet by 0.0929. A 1,000 sq ft space is about 92.9 square meters. For acres, divide square feet by 43,560 — a quarter-acre lot is 10,890 square feet.",
      },
      {
        q: "How do I convert cubic yards to cubic meters?",
        a: "Multiply cubic yards by 0.7646. A 5-yard concrete order is about 3.82 cubic meters. Since ready-mix is sold in yards in the US and meters elsewhere, this conversion shows up on every international spec sheet.",
      },
      {
        q: "What unit categories does the converter support?",
        a: "Eight: length, area, volume, weight, temperature, energy, pressure and speed — the set that covers construction estimates, material data sheets and equipment specs without wading through units nobody on a jobsite uses.",
      },
      {
        q: "Is the MatCalc360 converter really free?",
        a: "Yes — free forever, with no login, no signup and no paywall, like every tool on MatCalc360. Open the page and convert; that is the entire transaction.",
      },
    ],
    relatedSlugs: ["board-feet-calculator", "concrete-calculator", "paint-calculator"],
    cardBlurb: "Length, area, volume, weight, temperature and more — feet-inch friendly.",
    ctaText: "Try the Unit Converter",
  },

  "feet-inch-calculator": {
    slug: "feet-inch-calculator",
    projectType: "Tools & Converters",
    seoTitle: "Free Feet and Inches Calculator — Construction Math · No Login | MatCalc360",
    metaDescription:
      "Free feet and inches calculator — no login, no signup. Add, subtract, multiply and divide feet-inch-fraction measurements, solve equations and run scientific functions on one construction calculator.",
    kicker: "MC360 // Tools & Converters",
    h1Lines: [
      [{ t: "THE CALCULATOR THAT" }],
      [{ t: "SPEAKS ", accent: true }, { t: "FEET" }],
      [{ t: "AND INCHES." }],
    ],
    intro:
      "7′ 4¼″ plus 3′ 9⅝″ — construction math breaks normal calculators. This one handles feet, inches and fractions natively, with scientific functions and equation solving when the takeoff gets exotic. Free, no login.",
    heroImage: IMG("photo-1581094794329-c8112a89af12"),
    heroImageAlt: "Engineer reviewing calculations on a tablet at a job site",
    primaryCta: { label: "Try the Calculator", href: `${APP}/?tab=calc` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Feet & Inches Calculator",
      path: "/?tab=calc",
      inputs: [
        { label: "Entry", value: "7′ 4¼″ + 3′ 9⅝″" },
        { label: "Mode", value: "Feet-Inch" },
        { label: "Function", value: "Scientific" },
        { label: "Equation", value: "2x + 3 = 7" },
      ],
      outputs: [
        { label: "Result", value: "11′ 1⅞″", highlight: true },
        { label: "Decimal", value: "11.156 ft" },
        { label: "Equation", value: "x = 2" },
        { label: "Modes", value: "3 in 1" },
      ],
    },
    guideTitle: "CONSTRUCTION MATH WITHOUT THE HEADACHE",
    chapters: [
      {
        heading: "Feet-inch-fraction is the native language",
        body: "Standard calculators force you to convert everything to decimals and back — and that is where quarter-inch errors sneak into a cut list. This calculator accepts 7′ 4¼″ directly, adds it to 3′ 9⅝″, and answers 11′ 1⅞″ without a single manual conversion.",
        points: ["Type measurements as you read the tape", "Fractions stay fractions — no decimal drift", "Add, subtract, multiply, divide"],
      },
      {
        heading: "Scientific functions on standby",
        body: "Trigonometry for roof angles, square roots for diagonal bracing, powers for volume work — the scientific mode covers the math behind stair layouts, rafter cuts and slope conversions without reaching for a second app.",
        points: ["Trig for rafter and stair angles", "Square roots for diagonals", "Powers and roots built in"],
      },
      {
        heading: "Equation solving when the plan is vague",
        body: "When the drawing gives you a relationship instead of a number — 2x + 3 = 7 — the equation solver isolates the unknown directly. Solve for the missing dimension, the pitch variable or the spacing value right where you are standing.",
        points: ["Solve linear equations instantly", "Find missing dimensions on site", "No separate algebra step"],
      },
    ],
    visualImage: IMG("photo-1596495577886-d920f1fb7238"),
    visualImageAlt: "Equations written on a whiteboard during planning",
    visualStat: { kicker: "ONE ENTRY, NO CONVERSION", value: "7′ 4¼″", label: "typed exactly as the tape reads it — fractions included, decimals optional" },
    faqs: [
      {
        q: "How do you add feet and inches together?",
        a: "Convert everything to inches, add, then convert back — or skip the busywork entirely. 7′ 4¼″ + 3′ 9⅝″ is 11′ 1⅞″, and the feet-inch calculator produces that directly from the tape-measure reading, fractions and all.",
      },
      {
        q: "How do I convert feet and inches to decimal feet?",
        a: "Divide the inches by 12 and add to the feet: 8′ 6″ is 8.5 feet, and 10′ 3″ is 10.25 feet. Decimal feet are what most material formulas expect — the calculator shows both formats for every result.",
      },
      {
        q: "Can the calculator solve equations?",
        a: "Yes. Enter a linear equation like 2x + 3 = 7 and it solves for the unknown. It is built for the moments when a plan gives you a relationship between dimensions instead of the dimension itself.",
      },
      {
        q: "Is the MatCalc360 calculator free?",
        a: "Completely free — no login, no signup, no paywall. The same is true of every tool on MatCalc360, from the roofing calculator to the unit converter.",
      },
    ],
    relatedSlugs: ["unit-converter", "board-feet-calculator", "concrete-calculator"],
    cardBlurb: "Feet-inch-fraction math, scientific functions and equation solving in one calculator.",
    ctaText: "Try the Calculator",
  },

  "construction-level": {
    slug: "construction-level",
    projectType: "Tools & Converters",
    seoTitle: "Free Construction Level — Phone Bubble Level for Pitch & Slope · No Login | MatCalc360",
    metaDescription:
      "Free construction level — no login, no signup. Turn your phone into a bubble level: pitch, percent grade, rise/run, inches per foot, plumb and slope readings right on the jobsite.",
    kicker: "MC360 // Tools & Converters",
    h1Lines: [
      [{ t: "A BUBBLE LEVEL" }],
      [{ t: "ALREADY IN", accent: true }],
      [{ t: "YOUR POCKET." }],
    ],
    intro:
      "The level is always in the truck. Your phone is not. The MatCalc360 construction level reads pitch, percent grade, rise-over-run, inches-per-foot and plumb from your phone's sensors — calibrated, jobsite-ready, free with no login.",
    heroImage: IMG("photo-1565008447742-97f6f38c985c"),
    heroImageAlt: "High-rise structure under construction against the sky",
    primaryCta: { label: "Try the Construction Level", href: `${APP}/?tab=level` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Construction Level",
      path: "/?tab=level",
      inputs: [
        { label: "Surface", value: "Roof deck" },
        { label: "Reading Mode", value: "Pitch" },
        { label: "Sensor", value: "Phone gyro" },
        { label: "Calibration", value: "Done ✓" },
      ],
      outputs: [
        { label: "Pitch", value: "6/12", highlight: true },
        { label: "Angle", value: "26.6°" },
        { label: "Grade", value: "50 %" },
        { label: "Rise per Foot", value: "6 in" },
      ],
    },
    guideTitle: "PITCH, GRADE AND PLUMB — FROM A PHONE",
    chapters: [
      {
        heading: "Every slope unit trades actually use",
        body: "Roofers talk in pitch, civil crews in percent grade, carpenters in inches per foot, and engineers in degrees. The level reads your surface once and reports all of them, so the roof deck you just measured as 6/12 is also 26.6 degrees and a 50% grade — no conversion chart required.",
        points: ["Pitch (x/12) for roofing", "% grade for drainage and paving", "Degrees and rise/run included"],
      },
      {
        heading: "Plumb and level without the torpedo",
        body: "Stand the phone on edge against a stud for plumb, lay it flat on a countertop for level, or set it on a stair stringer for pitch. The big high-contrast readout is designed to be read in full sun — pair it with the light mode toggle on this site.",
        points: ["Edge mode: plumb walls and posts", "Flat mode: level slabs and counters", "Sun-readable high-contrast readout"],
      },
      {
        heading: "Calibrate once, trust it all day",
        body: "Every phone sensor has a small offset. Set the phone on any known-flat surface, tap calibrate, and the level zeros itself against reality instead of the factory spec. Recalibrate after a hard drop and the readings stay honest.",
        points: ["Zero against any flat surface", "Recalibrate after drops", "Case on or off — calibrate as used"],
      },
    ],
    visualImage: IMG("photo-1513467535987-fd81bc7d62f8"),
    visualImageAlt: "Sparks from an angle grinder on a construction tool",
    visualStat: { kicker: "ONE SURFACE, FOUR READINGS", value: "6/12", label: "pitch — also 26.6°, 50% grade and 6 inches of rise per foot" },
    faqs: [
      {
        q: "How accurate is a phone level?",
        a: "Modern phone gyroscopes and accelerometers read within about a tenth of a degree after calibration — plenty for checking pitch, grade, plumb and level on site. For layout-grade tolerance work, verify with a quality 4-foot level, but for the hundred daily checks, the phone is honestly good enough.",
      },
      {
        q: "How do I calibrate the construction level?",
        a: "Place the phone on any surface you know is flat — a saw table, a countertop, a verified slab — and tap calibrate. The tool zeros its sensor offset against that reference. Calibrate with your case on if you use one, and redo it after the phone takes a fall.",
      },
      {
        q: "What is the difference between pitch, degrees and percent grade?",
        a: "They describe the same slope three ways. Pitch is inches of rise per 12 inches of run (roofing), percent grade is rise divided by run times 100 (roads and drainage), and degrees is the angle itself. A 6/12 pitch equals a 50% grade equals 26.6 degrees — the level shows all three.",
      },
      {
        q: "Can I measure roof pitch without climbing the roof?",
        a: "Yes — two ways. Hold the phone against a rafter or the underside of the decking in the attic, or measure pitch at the gable end from a ladder. The reading feeds straight into the MatCalc360 roofing calculator for the full material takeoff.",
      },
    ],
    relatedSlugs: ["roofing-calculator", "feet-inch-calculator", "unit-converter"],
    cardBlurb: "Pitch, % grade, rise/run, plumb and slope from your phone's sensors.",
    ctaText: "Try the Construction Level",
  },

  "drone-measurement": {
    slug: "drone-measurement",
    projectType: "Tools & Converters",
    seoTitle: "Free Drone Measurement Tool — Distance & Area from Video · No Login | MatCalc360",
    metaDescription:
      "Free drone measurement tool — no login, no signup. Upload MP4 or MOV drone footage, calibrate scale and tap points to measure linear feet, square feet and acres on any site.",
    kicker: "MC360 // Tools & Converters",
    h1Lines: [
      [{ t: "MEASURE THE SITE" }],
      [{ t: "FROM ", accent: true }, { t: "THE AIR." }],
    ],
    intro:
      "Roofs, lots, stockpiles — the tape measure loses to anything big, steep or dangerous. Upload drone footage, calibrate against one known dimension, and tap points on the video to pull linear feet, square footage and acreage. Free, no login.",
    heroImage: IMG("photo-1473968512647-3e447244af8f"),
    heroImageAlt: "Camera drone flying over a forested site",
    primaryCta: { label: "Try Drone Measurement", href: `${APP}/?tab=drone` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Drone Measurement",
      path: "/?tab=drone",
      inputs: [
        { label: "Footage", value: "MP4 · 4K" },
        { label: "Reference", value: "6 ft fence" },
        { label: "Calibration", value: "2 points" },
        { label: "Subject", value: "Roof planes" },
      ],
      outputs: [
        { label: "Ridge Length", value: "42.3 ft" },
        { label: "Lot Area", value: "0.31 acres", highlight: true },
        { label: "Perimeter", value: "412 LF" },
        { label: "Export", value: "Send-to-Calc" },
      ],
    },
    guideTitle: "FROM FOOTAGE TO FIELD MEASUREMENTS",
    chapters: [
      {
        heading: "Calibrate against one known dimension",
        body: "Every measurement starts with a truth: pick anything in frame whose real size you know — a 6-foot fence panel, a 10-foot gate, a standard door — and mark its two ends. That single calibration converts every other pixel distance on the same plane into feet.",
        points: ["Mark one known-length object", "Bigger references calibrate better", "Recalibrate per plane or altitude"],
      },
      {
        heading: "Tap points, get distances and areas",
        body: "Tap along a ridge line for linear feet, around a roof plane or lot boundary for square footage, or across a parcel for acreage. The tool totals each path as you go, so a full roof outline is minutes of tapping instead of an afternoon on a ladder.",
        points: ["Linear paths: ridges, eaves, fences", "Closed paths: roof planes, lots", "Acreage for parcels and fields"],
      },
      {
        heading: "Send measurements straight to a calculator",
        body: "Measured numbers rarely stop at measuring — they feed a material order. Send-to-Calc pushes your dimensions directly into the matching MatCalc360 calculator with the fields pre-filled, so a measured roof becomes a shingle count in one hop.",
        points: ["Pre-fills the target calculator", "Roof → roofing calculator", "Lot → stone or asphalt calculator"],
      },
    ],
    visualImage: IMG("photo-1508614589041-895b88991e3e"),
    visualImageAlt: "Drone hovering against a dark rocky landscape",
    visualStat: { kicker: "ONE CALIBRATION UNLOCKS", value: "412 LF", label: "of measured perimeter — from a single known six-foot fence panel" },
    faqs: [
      {
        q: "How does drone video measurement work?",
        a: "You upload an MP4 or MOV clip, mark the two ends of one object in frame whose real-world length you know, and the tool uses that scale to convert every other distance on the same plane. Tap points to trace lengths, perimeters and areas directly on the video.",
      },
      {
        q: "How accurate is measuring from drone footage?",
        a: "Accuracy depends on calibration quality and camera angle. With a clear reference object and near-vertical footage, expect within a few percent — more than adequate for material estimates, bid walks and progress documentation. It does not replace a survey for legal boundaries.",
      },
      {
        q: "What video formats can I upload?",
        a: "Standard MP4 and MOV files from any consumer or commercial drone. Higher resolution footage and slower, steadier passes produce the cleanest frames to measure from.",
      },
      {
        q: "Can I measure a roof without getting on a ladder?",
        a: "That is the most popular use. Fly a slow pass over the roof, calibrate against a known dimension like a fence panel or parking stripe, then trace each roof plane. Send the dimensions straight to the roofing calculator for squares and bundles.",
      },
    ],
    relatedSlugs: ["roofing-calculator", "stone-calculator", "construction-level"],
    cardBlurb: "Measure linear feet, square footage and acres from MP4/MOV drone footage.",
    ctaText: "Try Drone Measurement",
  },

  "time-and-wage-tracker": {
    slug: "time-and-wage-tracker",
    projectType: "Business & Site Tools",
    seoTitle: "Free Time Card Calculator — Weekly Time & Wage Tracker · No Login | MatCalc360",
    metaDescription:
      "Free time and wage tracker — no login, no signup. Clock weekly hours, convert time card entries, calculate overtime and estimate gross pay for contractors and crews.",
    kicker: "MC360 // Business & Site Tools",
    h1Lines: [
      [{ t: "HOURS IN." }],
      [{ t: "WAGES OUT.", accent: true }],
    ],
    intro:
      "Friday afternoon should not be an algebra exam. Enter start times, end times and hourly rate — the time and wage tracker totals the week in decimal hours, flags overtime past 40, and estimates gross pay. Free, no login, no spreadsheet.",
    heroImage: IMG("photo-1621905251189-08b45d6a269e"),
    heroImageAlt: "Tradesman working on site during a scheduled shift",
    primaryCta: { label: "Try the Time & Wage Tracker", href: `${APP}/?tab=timewage` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Time & Wage Tracker",
      path: "/?tab=timewage",
      inputs: [
        { label: "Hourly Rate", value: "$28.50" },
        { label: "Mon – Thu", value: "8 h / day" },
        { label: "Friday", value: "6 h 30 m" },
        { label: "Week", value: "Aug 10 – 16" },
      ],
      outputs: [
        { label: "Total Hours", value: "38.5 h" },
        { label: "Gross Pay", value: "$1,097.25", highlight: true },
        { label: "Overtime", value: "0 h" },
        { label: "Decimal Format", value: "7:45 = 7.75" },
      ],
    },
    guideTitle: "TIME CARDS THAT ADD THEMSELVES UP",
    chapters: [
      {
        heading: "Clock math without the conversion chart",
        body: "Time cards live in hours and minutes; payroll lives in decimal hours. Forty-five minutes is 0.75, twenty minutes is 0.33, and every manual conversion is a rounding error waiting to short someone. The tracker converts each day as you enter it.",
        points: ["7:45 worked = 7.75 hours", "Enter start/end or total per day", "Breaks deducted as you go"],
      },
      {
        heading: "Overtime flags itself at 40",
        body: "Federal rules pay time-and-a-half past 40 hours in a workweek, and many states add daily overtime on top. The tracker separates regular and overtime hours automatically so the wage estimate applies the right multiplier to the right hours.",
        points: ["OT starts past 40 h / week", "1.5× rate applied automatically", "Regular and OT hours itemized"],
      },
      {
        heading: "A week of wages in one view",
        body: "Hours times rate is simple once the hours are honest. The weekly summary shows total hours, overtime split and gross pay estimate per person — the number to sanity-check before payroll runs, not after the checks clear.",
        points: ["Weekly gross pay estimate", "Per-day and per-week totals", "Records ready for invoicing"],
      },
    ],
    visualImage: IMG("photo-1541888946425-d81bb19240f5"),
    visualImageAlt: "Construction cranes at sunrise at the start of a workday",
    visualStat: { kicker: "THE CONVERSION THAT SHORTS PAYCHECKS", value: "0.75", label: "of an hour in 45 minutes — decimal time, done right, every week" },
    faqs: [
      {
        q: "How do I convert time card hours to decimal?",
        a: "Divide the minutes by 60 and add to the hours: 7 hours 45 minutes is 7.75, and 6 hours 20 minutes is 6.33. Payroll multiplies decimal hours by rate, so this conversion is the step that decides whether the paycheck is right.",
      },
      {
        q: "How is overtime calculated for hourly workers?",
        a: "Federally, hours past 40 in a workweek pay at least 1.5 times the regular rate. Someone working 44 hours at $28.50 earns 40 × $28.50 plus 4 × $42.75 — $1,311 gross. Some states add daily overtime rules on top of the weekly threshold.",
      },
      {
        q: "Can I use this as a weekly time card for my crew?",
        a: "Yes — run it per worker, per week. Enter each day's hours and the tracker totals regular time, overtime and estimated gross pay, giving you a clean record for payroll or invoicing without building a spreadsheet.",
      },
      {
        q: "Is the time and wage tracker free?",
        a: "Free forever, no login, no signup — like every MatCalc360 tool. Open it Friday, total the week, close it. That is the entire workflow.",
      },
    ],
    relatedSlugs: ["feet-inch-calculator", "unit-converter", "storage-building-calculator"],
    cardBlurb: "Weekly hours, overtime splits and gross pay estimates for crews and contractors.",
    ctaText: "Try the Time & Wage Tracker",
  },

  "asphalt-paving-calculator": {
    slug: "asphalt-paving-calculator",
    projectType: "Paving & Sitework",
    seoTitle: "Free Asphalt Paving Calculator — Tonnage & Truckloads · No Login | MatCalc360",
    metaDescription:
      "Free asphalt paving calculator — no login, no signup. Estimate paving tonnage for driveways and parking lots by area, lifts and compacted depth. Tons, cubic yards and truckloads instantly.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "PAVING MATH," }],
      [{ t: "TON ", accent: true }, { t: "BY TON." }],
    ],
    intro:
      "How much asphalt do I need to pave it? Parking lot or driveway, the answer comes in tons from the plant. Enter area, lifts and compacted depth — the paving calculator returns tonnage, volume and the truck count to keep the paver moving.",
    heroImage: IMG("photo-1465447142348-e9952c393450"),
    heroImageAlt: "Aerial view of freshly paved asphalt lanes and interchange",
    primaryCta: { label: "Try the Paving Calculator", href: `${APP}/?tab=asphalt&sub=paving` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Asphalt Paving Calculator",
      path: "/?tab=asphalt&sub=paving",
      inputs: [
        { label: "Lot Length", value: "80 ft" },
        { label: "Lot Width", value: "30 ft" },
        { label: "Lifts", value: "2 × 2 in" },
        { label: "Mix Density", value: "145 lb/ft³" },
      ],
      outputs: [
        { label: "Paved Area", value: "2,400 sq ft" },
        { label: "Tonnage", value: "58.2 tons", highlight: true },
        { label: "Compacted Depth", value: "4 in" },
        { label: "Truckloads", value: "3" },
      ],
    },
    guideTitle: "PAVING ESTIMATES THE PLANT UNDERSTANDS",
    chapters: [
      {
        heading: "Square yards in, tons out",
        body: "Paving starts with coverage: length times width gives area, and area times compacted depth gives volume. Multiply by the mix density — about 145 pounds per cubic foot — and divide by 2,000. That tonnage is the language the asphalt plant actually speaks.",
        points: ["Area × depth × 145 lb/ft³ ÷ 2,000 = tons", "Report area in square yards too", "Density adjustable per mix design"],
      },
      {
        heading: "Lifts: build depth in layers",
        body: "Structural pavement goes down in lifts, not one thick mat. Two 2-inch lifts compact better than one 4-inch pour, and each lift's tonnage is figured separately. Driveways run one or two lifts; parking lots with truck traffic earn three.",
        points: ["Driveways: 1–2 lifts, 2.5–4 in total", "Lots: 2–3 lifts over base", "Each lift compacts ~25%"],
      },
      {
        heading: "Trucking keeps the paver honest",
        body: "A standard tri-axle carries 15–20 tons of hot mix, and the paver should never stop waiting on a truck. The calculator converts tonnage into truckloads so you can stage deliveries — a stopped paver means cold seams and a rougher mat.",
        points: ["≈ 15–20 tons per truckload", "Stage trucks ahead of the paver", "Round up — plants sell full loads"],
      },
    ],
    visualImage: IMG("photo-1469854523086-cc02fe5d8800"),
    visualImageAlt: "Long paved asphalt road through open country",
    visualStat: { kicker: "THE PAVING CONSTANT", value: "145", label: "pounds per cubic foot of compacted hot-mix — the density behind every tonnage figure" },
    faqs: [
      {
        q: "How much asphalt do I need to pave a driveway?",
        a: "A 12×50 ft single-car driveway at 3 inches compacted takes about 11 tons; a 20×50 ft double-wide needs about 18 tons. Enter your exact dimensions and lift plan in the paving calculator for the precise tonnage and truck count.",
      },
      {
        q: "How many square feet does a ton of asphalt pave?",
        a: "Roughly 80 square feet at 2 inches compacted, 55 at 3 inches, and 40 at 4 inches. Commercial lots paved in lifts should be figured lift by lift — total depth is what drives the tonnage.",
      },
      {
        q: "What is a lift in asphalt paving?",
        a: "A lift is one compacted layer of hot mix, usually 1.5 to 3 inches thick. Thick pavements are built from multiple lifts because each layer compacts properly only up to a few inches — a single thick pour stays soft inside and ruts early.",
      },
      {
        q: "How thick should a parking lot be paved?",
        a: "Passenger-car areas perform at 3–4 inches over a compacted stone base; truck lanes and dumpster pads want 5–7 inches or a concrete pad. The base carries the load — asphalt is the wearing surface, not the structure.",
      },
      {
        q: "How long before you can drive on new asphalt?",
        a: "Keep traffic off for 24–72 hours depending on temperature, and avoid turning steering wheels while stationary for the first week. Full cure takes 6–12 months — that is why sealcoating waits until the following season.",
      },
    ],
    relatedSlugs: ["asphalt-calculator", "asphalt-sealer-calculator", "stone-calculator"],
    cardBlurb: "Paving tonnage, lifts and truckloads for driveways and parking lots.",
    ctaText: "Try the Paving Calculator",
  },

  "asphalt-curbing-calculator": {
    slug: "asphalt-curbing-calculator",
    projectType: "Paving & Sitework",
    seoTitle: "Free Asphalt Curb Calculator — Extruded Curbing Tonnage · No Login | MatCalc360",
    metaDescription:
      "Free asphalt curbing calculator — no login, no signup. Estimate extruded asphalt curb: 5 profiles, trapezoid cross-section math, linear feet to tons and truckload counts.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "CURB LINES," }],
      [{ t: "MEASURED IN", accent: true }],
      [{ t: "TRUCKLOADS." }],
    ],
    intro:
      "How much asphalt does a curb line take? Extruded curbing is pure geometry: pick one of five profiles, measure linear feet, and the trapezoid math converts cross-section into cubic yards, tons and truckloads. Free, no login.",
    heroImage: IMG("photo-1517646287270-a5a9ca602e5c"),
    heroImageAlt: "Fresh asphalt edge and curb line on a paved road",
    primaryCta: { label: "Try the Curbing Calculator", href: `${APP}/?tab=asphalt&sub=curbing` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Asphalt Curbing Calculator",
      path: "/?tab=asphalt&sub=curbing",
      inputs: [
        { label: "Profile", value: "6×6 Slant" },
        { label: "Length", value: "850 LF" },
        { label: "Waste Factor", value: "5 %" },
        { label: "Mix", value: "Hot asphalt" },
      ],
      outputs: [
        { label: "Cross-Section", value: "0.21 ft²" },
        { label: "Volume", value: "6.6 cu yd" },
        { label: "Tonnage", value: "13.6 tons", highlight: true },
        { label: "Truckloads", value: "1" },
      ],
    },
    guideTitle: "EXTRUDED CURB: TRAPEZOIDS TO TRUCKLOADS",
    chapters: [
      {
        heading: "The trapezoid does the talking",
        body: "Every curb profile is a trapezoid: cross-sectional area equals (top width + base width) ÷ 2 × height. Multiply that area by the run in feet for cubic feet, divide by 27 for cubic yards. A 6×6 slant profile works out to about 0.21 square feet of asphalt per linear foot.",
        points: ["Area = (top + base) ÷ 2 × height", "Volume = area × linear feet", "6×6 slant ≈ 0.21 ft² per LF"],
      },
      {
        heading: "Five profiles, one formula",
        body: "Slant-face for lots, square for edging, mower-edge for lawns, D-shape for driveways, barrier for traffic control — the profile changes the cross-section, never the method. Pick the profile, measure the run, and the same trapezoid math produces the tonnage.",
        points: ["Slant, square, mower-edge, D, barrier", "Profile sets the ft² per foot", "Corners and curves stay in the run"],
      },
      {
        heading: "From linear feet to truckloads",
        body: "Curb tonnage surprises people: 850 feet of 6×6 slant is over 13 tons of hot mix. Convert volume with the mix density, add 5% for waste and machine cleanout, and round to truckload increments so the extruder never starves mid-run.",
        points: ["≈ 30 lb of mix per LF (6×6 slant)", "5% waste for startup and cleanout", "Keep the extruder fed continuously"],
      },
    ],
    visualImage: IMG("photo-1565008447742-97f6f38c985c"),
    visualImageAlt: "Construction site with heavy equipment and formed edges",
    visualStat: { kicker: "PER LINEAR FOOT", value: "0.21 ft²", label: "of asphalt cross-section in a 6×6 slant curb — trapezoid math in the field" },
    faqs: [
      {
        q: "How much asphalt do I need per foot of curb?",
        a: "It depends on the profile's cross-section. A 6×6 slant curb uses about 0.21 cubic feet — roughly 30 pounds of hot mix — per linear foot. Smaller mower-edge profiles run closer to 15 pounds per foot, and tall barrier profiles exceed 40.",
      },
      {
        q: "What is extruded asphalt curbing?",
        a: "A machine lays a continuous curb directly from hot-mix asphalt, extruding it through a profile die — no forms, no curing days, same material as the pavement around it. It goes down fast, bonds to the mat, and is the standard edging for commercial lots.",
      },
      {
        q: "What curb profiles does the calculator support?",
        a: "Five: slant-face, square, mower-edge, D-shape and barrier. Each has a different trapezoid cross-section, so the profile choice directly changes the tonnage per linear foot — the calculator applies the right geometry for each.",
      },
      {
        q: "How many feet of curb does a truckload lay?",
        a: "A 15-ton truckload lays roughly 1,000 linear feet of 6×6 slant curb. Smaller profiles stretch a load past 1,800 feet; barrier profiles cut it under 700. The calculator converts your run into both tonnage and truckloads.",
      },
      {
        q: "Asphalt curb or concrete curb?",
        a: "Asphalt curbing is faster and cheaper for lot edging and landscaping borders — placed the same day as paving. Concrete wins for structural duty: heavy traffic strikes, municipalities with code requirements, and anywhere snowplows work the edge hard.",
      },
    ],
    relatedSlugs: ["asphalt-paving-calculator", "asphalt-calculator", "stone-calculator"],
    cardBlurb: "Extruded curb tonnage by profile — trapezoid math plus truckload counts.",
    ctaText: "Try the Curbing Calculator",
  },

  "asphalt-sealer-calculator": {
    slug: "asphalt-sealer-calculator",
    projectType: "Paving & Sitework",
    seoTitle: "Free Sealcoating Calculator — Asphalt Sealer Gallons · No Login | MatCalc360",
    metaDescription:
      "Free sealcoating calculator — no login, no signup. Find out how much asphalt sealer you need: gallons per coat, 5-gallon buckets and total coverage for driveways and parking lots.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "SEAL THE DRIVE." }],
      [{ t: "BUY THE ", accent: true }, { t: "EXACT" }],
      [{ t: "GALLONS." }],
    ],
    intro:
      "How much sealer do I need for my driveway? Sealcoating is coverage math: square footage divided by the gallon rate, times the number of coats. Enter the area and get gallons, 5-gallon buckets and cure windows before you crack the first pail.",
    heroImage: IMG("photo-1513467535987-fd81bc7d62f8"),
    heroImageAlt: "Dark asphalt work with sparks from equipment at dusk",
    primaryCta: { label: "Try the Sealer Calculator", href: `${APP}/?tab=asphalt&sub=sealer` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Asphalt Sealer Calculator",
      path: "/?tab=asphalt&sub=sealer",
      inputs: [
        { label: "Driveway", value: "60 × 20 ft" },
        { label: "Coats", value: "2" },
        { label: "Coverage", value: "90 ft²/gal" },
        { label: "Method", value: "Squeegee" },
      ],
      outputs: [
        { label: "Sealed Area", value: "1,200 sq ft" },
        { label: "Sealer Needed", value: "27 gal", highlight: true },
        { label: "5-Gal Buckets", value: "6" },
        { label: "Traffic Ready", value: "24–48 h" },
      ],
    },
    guideTitle: "SEALCOATING BY THE GALLON, NOT THE GUESS",
    chapters: [
      {
        heading: "Coverage rates run the estimate",
        body: "One gallon of sealer covers 80–100 square feet per coat, sliding with surface age and texture. Old, thirsty, oxidized pavement drinks toward 70–80; a smooth, tight mat stretches past 100. Measure the area honestly, pick the rate that matches the surface, multiply by coats.",
        points: ["≈ 80–100 sq ft per gallon per coat", "Weathered surface: plan 70–80", "Smooth surface: 100+"],
      },
      {
        heading: "Two thin coats beat one thick one",
        body: "A single heavy coat skins over, tracks, and peels within a season. Two thin coats — the second applied perpendicular to the first after 4–6 hours — cure harder and last twice as long. The calculator defaults to two coats for a reason.",
        points: ["2 coats is the honest default", "Cross-pattern the second coat", "Wait 4–6 h between coats"],
      },
      {
        heading: "Prep decides the lifespan",
        body: "Sealer sticks to clean, dry, crack-filled pavement — nothing else. Power-wash, degrease oil spots, fill cracks over a quarter inch, then seal above 50°F with no rain for 24 hours. Skipping prep turns three years of protection into one.",
        points: ["Clean and crack-fill first", "50°F+ and dry for 24 h", "Reseal every 2–3 years"],
      },
    ],
    visualImage: IMG("photo-1469854523086-cc02fe5d8800"),
    visualImageAlt: "Sealed dark asphalt road stretching to the horizon",
    visualStat: { kicker: "THE COVERAGE RULE", value: "90 ft²", label: "per gallon per coat on typical pavement — the rate behind every sealer order" },
    faqs: [
      {
        q: "How much sealer do I need for a 2-car driveway?",
        a: "A typical 20×50 ft two-car driveway is 1,000 square feet — about 11 gallons per coat, or 22–25 gallons for the standard two coats. That is five 5-gallon buckets. Enter your exact dimensions in the sealer calculator for a precise count.",
      },
      {
        q: "How many square feet does a gallon of driveway sealer cover?",
        a: "Between 80 and 100 square feet per coat on average pavement. Rough, oxidized surfaces can drop coverage to 70 square feet per gallon on the first coat — which is exactly why the second coat always goes further.",
      },
      {
        q: "How long does sealcoating last?",
        a: "Two to three years with proper prep and two coats. Sun, snowplows and oil drips shorten that; shaded, well-drained driveways stretch it. If the surface has faded from black to gray, water is getting in — it is time.",
      },
      {
        q: "How long before I can drive on fresh sealer?",
        a: "Foot traffic after 4–6 hours, vehicles after 24–48 hours depending on heat and humidity. Cool, damp or shaded driveways need the long end — power-steering scuffs on uncured sealer are permanent.",
      },
      {
        q: "Spray or squeegee — does the method change the gallons?",
        a: "Slightly. Squeegee application works sealer into the pores and uses a touch more material on rough surfaces; spraying lays a thinner, more uniform film and stretches coverage toward 100 square feet per gallon. Pros often squeegee the first coat and spray the second.",
      },
    ],
    relatedSlugs: ["asphalt-paving-calculator", "asphalt-curbing-calculator", "asphalt-calculator"],
    cardBlurb: "Sealer gallons, buckets and coverage for driveways and lots.",
    ctaText: "Try the Sealer Calculator",
  },

  "mulch-calculator": {
    slug: "mulch-calculator",
    projectType: "Landscaping & Grounds",
    seoTitle: "Free Mulch Calculator — Cubic Yards & Bags · No Login | MatCalc360",
    metaDescription:
      "Free mulch calculator — no login, no signup. Find out how much mulch you need: cubic yards and bag counts from bed dimensions and depth. Bulk vs bagged figured instantly.",
    kicker: "MC360 // Landscaping & Grounds",
    h1Lines: [
      [{ t: "MULCH THE BEDS." }],
      [{ t: "SKIP THE ", accent: true }, { t: "FIFTH" }],
      [{ t: "TRIP TO THE YARD." }],
    ],
    intro:
      "How much mulch do I need? One cubic yard covers about 108 square feet at 3 inches deep — but beds are never square and bags never go as far as the label suggests. Enter bed dimensions and depth; get cubic yards and bag counts in one pass.",
    heroImage: IMG("photo-1649043601834-21dbcc89474b"),
    heroImageAlt: "Fresh shredded bark mulch spread in a landscape bed",
    primaryCta: { label: "Try the Mulch Calculator", href: `${APP}/?tab=landscaping` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Mulch Calculator",
      path: "/?tab=landscaping",
      inputs: [
        { label: "Bed Length", value: "40 ft" },
        { label: "Bed Width", value: "12 ft" },
        { label: "Depth", value: "3 in" },
        { label: "Purchase", value: "Bulk or bags" },
      ],
      outputs: [
        { label: "Bed Area", value: "480 sq ft" },
        { label: "Volume", value: "4.4 cu yd", highlight: true },
        { label: "2 cu ft Bags", value: "60" },
        { label: "3 cu ft Bags", value: "40" },
      ],
    },
    guideTitle: "MULCH MATH, FROM BEDS TO BAGS",
    chapters: [
      {
        heading: "Volume is area times depth",
        body: "Mulch starts with the same math as concrete: square footage times depth in feet, divided by 27, equals cubic yards. The shortcut worth memorizing — one cubic yard covers 162 square feet at 2 inches, 108 at 3 inches, and 81 at 4 inches deep.",
        points: ["1 cu yd ≈ 108 sq ft @ 3 in", "Beds: measure each, add together", "Depth in feet (3 in = 0.25 ft)"],
      },
      {
        heading: "Bags vs. bulk",
        body: "Bagged mulch comes in 2 and 3 cubic-foot bags — 13.5 of the 2 cu ft bags equal one cubic yard. Bags make sense under two yards or for spot work; beyond that, bulk delivery costs roughly half per yard and skips a trunkload of plastic.",
        points: ["13.5 × 2 cu ft bags = 1 cu yd", "9 × 3 cu ft bags = 1 cu yd", "Bulk wins above ~2 yards"],
      },
      {
        heading: "Depth rules that protect plants",
        body: "Two to three inches is the working range: enough to block weeds and hold moisture, not so much it suffocates roots. Keep mulch pulled back from trunks and stems — the volcano mound against bark invites rot and rodents, not curb appeal.",
        points: ["2–3 in for beds, 4 in max", "Keep mulch off trunks and stems", "Top up 1 in yearly, don't pile on"],
      },
    ],
    visualImage: IMG("photo-1708432331128-cfe5a2803781"),
    visualImageAlt: "Close texture of shredded hardwood mulch",
    visualStat: { kicker: "ONE CUBIC YARD COVERS", value: "108 ft²", label: "at the standard 3-inch depth — the number behind every mulch order" },
    faqs: [
      {
        q: "How much mulch do I need for 100 square feet?",
        a: "At the standard 3-inch depth, 100 square feet takes just under one cubic yard — about 0.93 yards, or thirteen 2 cu ft bags. At 2 inches it drops to 0.62 yards. Enter your bed dimensions in the mulch calculator for the exact figure.",
      },
      {
        q: "How many bags of mulch are in a cubic yard?",
        a: "Thirteen and a half 2 cu ft bags, or nine 3 cu ft bags. Since you cannot buy half a bag, round up — and past about two yards, bulk delivery almost always beats the per-bag price.",
      },
      {
        q: "How deep should mulch be?",
        a: "Two to three inches for most beds. Less than two lets weeds through; more than four suffocates roots and stays soggy. When refreshing, rake the old mulch and add only enough to bring the total back to three inches.",
      },
      {
        q: "Is bulk mulch cheaper than bagged?",
        a: "Usually significantly — bulk runs roughly half the per-yard cost of bagged once you pass two cubic yards, with no bags to haul or landfill. Bags still win for small touch-ups, tight access, or when you need several different colors.",
      },
      {
        q: "How often should I re-mulch?",
        a: "Once a year for dyed mulches that fade, every one to two years for natural bark. Top up with about an inch rather than a full new layer — mulch decomposes into soil, and endlessly stacking fresh layers buries root zones too deep.",
      },
    ],
    relatedSlugs: ["pinestraw-calculator", "stone-calculator", "grass-seed-calculator"],
    cardBlurb: "Cubic yards and bag counts for landscape beds, bulk or bagged.",
    ctaText: "Try the Mulch Calculator",
  },

  "pinestraw-calculator": {
    slug: "pinestraw-calculator",
    projectType: "Landscaping & Grounds",
    seoTitle: "Free Pine Straw Calculator — Bales Per Square Foot · No Login | MatCalc360",
    metaDescription:
      "Free pine straw calculator — no login, no signup. Find out how many bales of pine straw you need: bale counts from bed square footage with coverage rates for new installs and top-ups.",
    kicker: "MC360 // Landscaping & Grounds",
    h1Lines: [
      [{ t: "PINE STRAW BY" }],
      [{ t: "THE ", accent: true }, { t: "EXACT BALE." }],
    ],
    intro:
      "How many bales of pine straw do I need? One bale covers about 45 square feet at a fresh 2–3 inch depth — half that on a top-up. Enter bed square footage and the pine straw calculator returns the bale count for the install and the yearly refresh.",
    heroImage: IMG("photo-1441974231531-c6227db76b6e"),
    heroImageAlt: "Pine forest floor covered in fallen pine needles",
    primaryCta: { label: "Try the Pine Straw Calculator", href: `${APP}/?tab=landscaping&sub=pinestraw` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Pine Straw Calculator",
      path: "/?tab=landscaping&sub=pinestraw",
      inputs: [
        { label: "Bed Area", value: "900 sq ft" },
        { label: "Install Type", value: "New layer" },
        { label: "Coverage", value: "45 ft²/bale" },
        { label: "Depth", value: "2–3 in" },
      ],
      outputs: [
        { label: "Bales Needed", value: "20", highlight: true },
        { label: "Yearly Top-Up", value: "10 bales" },
        { label: "Depth", value: "2–3 in" },
        { label: "Refresh", value: "1–2× / yr" },
      ],
    },
    guideTitle: "BALES, COVERAGE AND THE SOUTHERN STANDARD",
    chapters: [
      {
        heading: "Coverage is the whole calculation",
        body: "A standard square bale of longleaf pine straw covers 40–50 square feet at a fresh 2–3 inch depth — call it 45 for estimating. Divide bed area by coverage and round up. Slash pine bales run looser and cover a bit less per bale.",
        points: ["≈ 45 sq ft per bale, new install", "Top-ups stretch to 80–90 sq ft/bale", "Longleaf covers best per bale"],
      },
      {
        heading: "Why pine straw owns the South",
        body: "Pine straw stays put on slopes where bark mulch floats away, acidifies soil gently for azaleas and camellias, and costs less per square foot than bagged mulch in pine country. It is also lighter — one person can spread a bale in minutes.",
        points: ["Holds slopes better than bark", "Slight acidity suits azaleas", "Cheaper per ft² in pine regions"],
      },
      {
        heading: "Refresh, don't bury",
        body: "Pine straw compacts and fades to gray within months — that is normal, not failure. Top up with a thin fresh layer once or twice a year to restore color and depth instead of stripping beds. Old needles break down into the soil on their own.",
        points: ["Color fades in 3–6 months", "Top up 1–2× per year", "No need to remove old layers"],
      },
    ],
    visualImage: IMG("photo-1509316975850-ff9c5deb0cd9"),
    visualImageAlt: "Tall pine trees against the sky",
    visualStat: { kicker: "ONE BALE COVERS", value: "45 ft²", label: "at a fresh 2–3 inch depth — the rate behind every pine straw order" },
    faqs: [
      {
        q: "How many bales of pine straw do I need for 1,000 square feet?",
        a: "About 22 bales for a new 2–3 inch install at 45 square feet per bale, or roughly 11–12 bales for a refresh top-up. The pine straw calculator figures both from your exact bed area.",
      },
      {
        q: "How much area does one bale of pine straw cover?",
        a: "Between 40 and 50 square feet at a fresh 2–3 inch depth for longleaf straw in square bales. Round bales and looser slash pine cover less — check what your supplier actually sells before comparing prices.",
      },
      {
        q: "Is pine straw better than mulch?",
        a: "On slopes and around acid-loving plants, yes — it locks together and resists washing out, and its mild acidity suits azaleas, camellias and blueberries. Bark mulch lasts longer between refreshes and looks tidier in formal beds. Many pros use pine straw on banks and mulch in show beds.",
      },
      {
        q: "How deep should pine straw be spread?",
        a: "Two to three inches on a new install — thick enough to shade out weeds and hold moisture, thin enough to let rain through. Keep it a few inches back from plant stems and house siding, just like any mulch.",
      },
      {
        q: "How often does pine straw need replacing?",
        a: "Plan on a light top-up once or twice a year. The needles themselves take two-plus years to decompose, but the fresh copper color fades within months, which is what most refreshes are really buying.",
      },
    ],
    relatedSlugs: ["mulch-calculator", "grass-seed-calculator", "straw-calculator"],
    cardBlurb: "Bale counts for pine straw installs and yearly top-ups.",
    ctaText: "Try the Pine Straw Calculator",
  },

  "grass-seed-calculator": {
    slug: "grass-seed-calculator",
    projectType: "Landscaping & Grounds",
    seoTitle: "Free Grass Seed Calculator — Pounds Per 1,000 Sq Ft · No Login | MatCalc360",
    metaDescription:
      "Free grass seed calculator — no login, no signup. Find out how much grass seed you need: pounds per 1,000 sq ft for new lawns and overseeding, by grass type. Fescue, rye, Bermuda and more.",
    kicker: "MC360 // Landscaping & Grounds",
    h1Lines: [
      [{ t: "SEED THE LAWN" }],
      [{ t: "AT THE ", accent: true }, { t: "RIGHT RATE." }],
    ],
    intro:
      "How much grass seed do I need? New lawns run 4–8 pounds per 1,000 square feet depending on the grass — overseeding takes half that. Enter square footage and grass type; the calculator returns pounds for the full seeding and the overseed rate.",
    heroImage: IMG("photo-1526392587392-d1627b6c134a"),
    heroImageAlt: "Thick healthy green lawn grass",
    primaryCta: { label: "Try the Grass Seed Calculator", href: `${APP}/?tab=landscaping&sub=grass` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Grass Seed Calculator",
      path: "/?tab=landscaping&sub=grass",
      inputs: [
        { label: "Lawn Area", value: "5,000 sq ft" },
        { label: "Grass Type", value: "Tall fescue" },
        { label: "Job", value: "New lawn" },
        { label: "Rate", value: "7 lb / 1,000 ft²" },
      ],
      outputs: [
        { label: "Seed Needed", value: "35 lb", highlight: true },
        { label: "Overseed Rate", value: "18 lb" },
        { label: "Coverage", value: "7 lb / 1,000 ft²" },
        { label: "Best Window", value: "Early fall" },
      ],
    },
    guideTitle: "SEEDING RATES WITHOUT THE BAG-READING",
    chapters: [
      {
        heading: "Pounds per thousand is the unit",
        body: "Every grass has a seeding rate in pounds per 1,000 square feet: tall fescue 6–8, perennial rye 5–8, Kentucky bluegrass 2–3, Bermuda just 1–2 because its seed is tiny. Measure the lawn, divide by 1,000, multiply by the rate — that is the order.",
        points: ["Fescue: 6–8 lb per 1,000 ft²", "Bluegrass: 2–3 lb per 1,000 ft²", "Bermuda: 1–2 lb per 1,000 ft²"],
      },
      {
        heading: "New lawn vs. overseed",
        body: "Overseeding an existing lawn takes half the new-lawn rate — the established grass fills the gaps. Bare dirt and full renovations get the full rate, and slopes or patchy germination justify bumping 10–15% above it rather than reseeding twice.",
        points: ["Overseed = half the full rate", "Bare soil = full rate +10%", "Slopes: bump for wash-off"],
      },
      {
        heading: "Timing beats rate",
        body: "The best seed at the wrong time loses to average seed at the right one. Cool-season grasses (fescue, rye, bluegrass) want early fall soil; warm-season grasses (Bermuda, zoysia) want late spring. Cover with straw or a seed mat and keep the top inch moist until germination.",
        points: ["Cool-season: seed in early fall", "Warm-season: late spring", "Keep top inch moist 2–3 weeks"],
      },
    ],
    visualImage: IMG("photo-1625246333195-78d9c38ad449"),
    visualImageAlt: "Young green shoots germinating in dark soil",
    visualStat: { kicker: "THE SEEDING UNIT", value: "7 lb", label: "of tall fescue per 1,000 square feet of new lawn — the rate behind every bag count" },
    faqs: [
      {
        q: "How much grass seed do I need for 1,000 square feet?",
        a: "For a new lawn: 6–8 lb of tall fescue, 5–8 lb of perennial rye, 2–3 lb of Kentucky bluegrass, or 1–2 lb of Bermuda. Overseeding an existing lawn takes about half those rates. The grass seed calculator applies the right rate per grass type.",
      },
      {
        q: "How much grass seed for a 5,000 sq ft lawn?",
        a: "A new 5,000 sq ft fescue lawn takes about 35 pounds of seed at 7 lb per 1,000 square feet — two big bags. Overseeding the same lawn takes roughly 17–18 pounds. Always measure the actual area; lot size minus house and beds is the honest number.",
      },
      {
        q: "Is more seed better when overseeding?",
        a: "No — overseeding past the recommended rate makes seedlings compete for light and water, producing a weaker, disease-prone stand. Follow the half-rate rule for overseeding and put the savings into starter fertilizer instead.",
      },
      {
        q: "When is the best time to plant grass seed?",
        a: "Early fall for cool-season grasses — warm soil, cool air, fewer weeds. Late spring for warm-season grasses once soil passes 65°F. Spring seeding of cool-season grass works but fights summer heat before roots establish.",
      },
      {
        q: "Should I cover grass seed with straw?",
        a: "Yes on bare soil — a light straw cover (about one bale per 800–1,000 sq ft) or a seed mat holds moisture and stops wash-off on slopes. The straw calculator and seed mat calculator size both options from the same lawn area.",
      },
    ],
    relatedSlugs: ["straw-calculator", "grass-seed-mat-calculator", "mulch-calculator"],
    cardBlurb: "Pounds per 1,000 sq ft for new lawns and overseeding, by grass type.",
    ctaText: "Try the Grass Seed Calculator",
  },

  "straw-calculator": {
    slug: "straw-calculator",
    projectType: "Landscaping & Grounds",
    seoTitle: "Free Straw Calculator — Bales for Seed Cover & Erosion · No Login | MatCalc360",
    metaDescription:
      "Free straw calculator — no login, no signup. Find out how many straw bales you need to cover grass seed or control erosion: bale counts from area with light and heavy coverage rates.",
    kicker: "MC360 // Landscaping & Grounds",
    h1Lines: [
      [{ t: "COVER THE SEED." }],
      [{ t: "COUNT ", accent: true }, { t: "THE BALES." }],
    ],
    intro:
      "How many straw bales do I need? One bale covers 800–1,000 square feet as a light seed blanket, or about 400 for serious erosion duty. Enter the area, pick the job, and get the bale count before the seed goes down.",
    heroImage: IMG("photo-1500382017468-9049fed747ef"),
    heroImageAlt: "Golden straw field at sunset after harvest",
    primaryCta: { label: "Try the Straw Calculator", href: `${APP}/?tab=landscaping&sub=straw` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Straw Calculator",
      path: "/?tab=landscaping&sub=straw",
      inputs: [
        { label: "Area", value: "4,000 sq ft" },
        { label: "Purpose", value: "Seed cover" },
        { label: "Rate", value: "1 bale / 800 ft²" },
        { label: "Depth", value: "≈ 1 in" },
      ],
      outputs: [
        { label: "Bales Needed", value: "5", highlight: true },
        { label: "Erosion Rate", value: "10 bales" },
        { label: "Coverage", value: "800 ft²/bale" },
        { label: "Remove Later", value: "No — decomposes" },
      ],
    },
    guideTitle: "STRAW COVER, FROM SEED BEDS TO SLOPES",
    chapters: [
      {
        heading: "Two rates: blanket or armor",
        body: "As a light blanket over fresh grass seed, one bale stretches 800–1,000 square feet — you want soil peeking through. For erosion control on bare slopes, coverage drops to about 400 square feet per bale, laid thick enough to hold against rain impact.",
        points: ["Seed cover: 1 bale / 800–1,000 ft²", "Erosion: 1 bale / ~400 ft²", "Light cover — see soil through it"],
      },
      {
        heading: "Straw, not hay — weed seeds matter",
        body: "Straw is the stalk left after grain harvest — mostly hollow stems, few seeds. Hay is cut forage full of seed heads, and spreading it on a new lawn plants a weed farm. Buy certified weed-free straw for seed cover; the few extra dollars are cheaper than a summer of pulling.",
        points: ["Straw = grain stalks, low seed", "Hay = forage, full of weed seed", "Ask for weed-free certified straw"],
      },
      {
        heading: "Leave it — it feeds the soil",
        body: "A light straw blanket decomposes in a season and becomes organic matter; there is no cleanup pass. Rake it only if it was laid too thick and smothers seedlings. On slopes, netting or tackifier over the straw keeps it in place until roots take over.",
        points: ["Decomposes in place", "Rake only if smothering sprouts", "Net slopes against wind and wash"],
      },
    ],
    visualImage: IMG("photo-1500076656116-558758c991c1"),
    visualImageAlt: "Barn with stacked straw bales at sunset",
    visualStat: { kicker: "ONE BALE BLANKETS", value: "800 ft²", label: "of fresh grass seed at the right light depth — thick enough to hold moisture, thin enough to see soil" },
    faqs: [
      {
        q: "How many straw bales do I need for grass seed?",
        a: "Figure one bale per 800–1,000 square feet for a light seed blanket — a 4,000 sq ft lawn takes about 5 bales. The cover should be thin enough to see half the soil through it; seedlings need light as much as moisture.",
      },
      {
        q: "How much does one bale of straw cover?",
        a: "Between 800 and 1,000 square feet at seed-cover depth, around 400 square feet at erosion-control depth, and about 100 square feet if you are using straw as a garden mulch laid several inches thick.",
      },
      {
        q: "What is the difference between straw and hay?",
        a: "Straw is the dried stalk after the grain is harvested — hollow, yellow, nearly seed-free. Hay is dried forage grass with seed heads intact. Straw covers seed beds; hay belongs in feed troughs and will seed your lawn with weeds.",
      },
      {
        q: "Do I need to remove straw after the grass grows?",
        a: "No — a properly light layer decomposes within a season and feeds the soil. Only rake it out if it went down thick enough to mat and smother seedlings, which is the most common straw mistake.",
      },
      {
        q: "Straw or a seed mat — which is better?",
        a: "Straw is cheaper per square foot on flat, open ground. Seed mats win on slopes, small patches and windy sites because the blanket stays put without netting. Price both — the straw calculator and seed mat calculator run from the same area.",
      },
    ],
    relatedSlugs: ["grass-seed-calculator", "grass-seed-mat-calculator", "pinestraw-calculator"],
    cardBlurb: "Bale counts for grass seed cover and erosion control.",
    ctaText: "Try the Straw Calculator",
  },

  "grass-seed-mat-calculator": {
    slug: "grass-seed-mat-calculator",
    projectType: "Landscaping & Grounds",
    seoTitle: "Free Seed Mat Calculator — Rolls & Blanket Coverage · No Login | MatCalc360",
    metaDescription:
      "Free grass seed mat calculator — no login, no signup. Find out how many seed blanket rolls you need: roll counts from area with overlap, plus landscape stake estimates for slopes and patches.",
    kicker: "MC360 // Landscaping & Grounds",
    h1Lines: [
      [{ t: "ROLL OUT" }],
      [{ t: "THE ", accent: true }, { t: "NEW LAWN." }],
    ],
    intro:
      "How many seed mat rolls do I need? A standard 4×50 ft roll covers 200 square feet — but overlaps eat 10% of that. Enter the area and roll size; the calculator returns roll count and the stakes to pin it down, slopes included.",
    heroImage: IMG("photo-1589793929550-f29a6b7e4a32"),
    heroImageAlt: "Fresh young grass growing in sunlight",
    primaryCta: { label: "Try the Seed Mat Calculator", href: `${APP}/?tab=landscaping&sub=seedmat` },
    secondaryCta: { label: "See how it works", href: "#how-it-works" },
    calculator: {
      name: "Grass Seed Mat Calculator",
      path: "/?tab=landscaping&sub=seedmat",
      inputs: [
        { label: "Area", value: "30 × 20 ft" },
        { label: "Roll Size", value: "4 × 50 ft" },
        { label: "Overlap", value: "10 %" },
        { label: "Slope", value: "Moderate" },
      ],
      outputs: [
        { label: "Rolls Needed", value: "4", highlight: true },
        { label: "Coverage", value: "200 ft²/roll" },
        { label: "Stakes", value: "≈ 60" },
        { label: "Water", value: "Daily till sprout" },
      ],
    },
    guideTitle: "SEED BLANKETS: ROLLS, OVERLAP AND STAKES",
    chapters: [
      {
        heading: "Rolls cover less than the label",
        body: "A 4×50 ft roll is 200 square feet on paper, but edges overlap 3–4 inches and ends tuck — plan 10% overlap in every estimate. A 600 sq ft slope therefore takes four rolls, not three. The calculator builds the overlap in automatically.",
        points: ["4×50 roll = 200 ft² nominal", "Plan 10% for overlap", "Odd shapes add another 5%"],
      },
      {
        heading: "Where mats beat loose straw",
        body: "Seed mats and erosion blankets shine where loose cover fails: slopes, swales, windy sites and small patches. The biodegradable netting holds seed and fiber in place through watering and storms — no tackifier, no netting, no chasing straw across the yard.",
        points: ["Slopes and swales: mat wins", "Windy sites stay covered", "No separate netting needed"],
      },
      {
        heading: "Stake it or lose it",
        body: "Every seam, edge and roll end gets a landscape stake every 2–3 feet — roughly 15 stakes per roll on flat ground, more on slopes. Then water daily until germination: the mat holds moisture, but it cannot create it.",
        points: ["≈ 15 stakes per roll, flat", "More stakes on slopes", "Daily water until sprout"],
      },
    ],
    visualImage: IMG("photo-1476231682828-37e571bc172f"),
    visualImageAlt: "Sunlight breaking through trees onto new growth",
    visualStat: { kicker: "ONE ROLL COVERS", value: "200 ft²", label: "at 4×50 ft before overlap — the unit behind every seed blanket order" },
    faqs: [
      {
        q: "How many seed mat rolls do I need?",
        a: "Divide your area by the roll coverage — 200 square feet for a standard 4×50 ft roll — then add 10% for overlap. A 600 sq ft area takes 4 rolls. Odd-shaped patches should add another 5% for cutting waste.",
      },
      {
        q: "What is a grass seed mat?",
        a: "A biodegradable blanket with seed (or a plain fiber blanket you seed under) held between fine netting. Rolled over prepared soil and staked down, it keeps seed moist and in place until germination — especially on slopes where loose straw washes off.",
      },
      {
        q: "Is a seed mat better than straw?",
        a: "On slopes, small patches and windy sites, yes — it stays put without extra netting and gives very even germination. On large flat areas, loose straw is cheaper per square foot. The straw calculator and seed mat calculator price both from the same measurements.",
      },
      {
        q: "How do I install a seed blanket?",
        a: "Rake the soil smooth, seed if using a plain blanket, roll the mat out with edges overlapping 3–4 inches, and stake every 2–3 feet along all seams and edges. Water daily — lightly and often — until the grass is up and rooted through the netting.",
      },
      {
        q: "Does the seed mat need to be removed later?",
        a: "No — the netting and fiber are biodegradable and break down as the lawn establishes, usually within one to two seasons. Do not pull it up early; the grass roots through it within weeks.",
      },
    ],
    relatedSlugs: ["grass-seed-calculator", "straw-calculator", "mulch-calculator"],
    cardBlurb: "Seed blanket roll counts with overlap and stake estimates.",
    ctaText: "Try the Seed Mat Calculator",
  },
};

// Keyword alias pages: own SEO title/meta/H1/intro/FAQs targeting synonym searches,
// sharing the parent's calculator preview, chapters and imagery. Same real tool behind each.
const ALIASES = {
  "gravel-calculator": {
    ...PAGES["stone-calculator"],
    slug: "gravel-calculator",
    alias: true,
    primaryCta: { label: "Try the Gravel Calculator", href: `${APP}/?tab=stone` },
    seoTitle: "Free Gravel Calculator — Tons & Cubic Yards for Driveways · No Login | MatCalc360",
    metaDescription:
      "Free gravel calculator — no login, no signup. Find out how much gravel you need for a driveway, walkway or base: tons and cubic yards from area and depth, compaction included.",
    kicker: "MC360 // Paving & Sitework",
    h1Lines: [
      [{ t: "HOW MUCH GRAVEL?" }],
      [{ t: "EXACTLY ", accent: true }, { t: "THIS MUCH." }],
    ],
    intro:
      "How much gravel do I need for a driveway? Measure length, width and depth — the gravel calculator converts that hole into cubic yards and tons, with compaction factored in so the delivered load matches the finished base.",
    faqs: [
      {
        q: "How much gravel do I need for a driveway?",
        a: "A 12×50 ft single-car driveway at 4 inches deep takes about 7.4 cubic yards — roughly 10 tons of gravel, or 11 with compaction allowance. Enter your own dimensions in the gravel calculator for the exact figure.",
      },
      {
        q: "How many square feet does a ton of gravel cover?",
        a: "One ton covers about 110 square feet at 2 inches deep, 55 square feet at 4 inches, and 37 square feet at 6 inches. Depth is where most gravel orders go wrong — decide the compacted depth first, then order.",
      },
      {
        q: "What is the difference between pea gravel and crushed stone?",
        a: "Pea gravel is rounded and never compacts — it is decorative, for paths and beds. Crushed stone (ABC, crusher run, #57) has angular edges and fines that lock together under a compactor, making it the only right answer under driveways and slabs.",
      },
      {
        q: "How deep should gravel be for a driveway?",
        a: "Four to six inches of compacted crushed stone handles normal vehicles on decent soil. Soft ground or heavy trucks justify 8 inches, placed in compacted lifts of no more than 4 inches each.",
      },
    ],
    relatedSlugs: ["stone-calculator", "asphalt-calculator", "concrete-calculator"],
    cardBlurb: "Gravel tonnage and yardage for driveways, walkways and bases.",
    ctaText: "Try the Gravel Calculator",
  },

  "sheetrock-calculator": {
    ...PAGES["drywall-calculator"],
    slug: "sheetrock-calculator",
    alias: true,
    primaryCta: { label: "Try the Sheetrock Calculator", href: `${APP}/?tab=drywall` },
    seoTitle: "Free Sheetrock Calculator — Sheets Per Room · No Login | MatCalc360",
    metaDescription:
      "Free sheetrock calculator — no login, no signup. Find out how many sheets of sheetrock you need for any room: 4×8 or 4×12 counts with mud, tape, screws and waste included.",
    kicker: "MC360 // Interiors & Drywall",
    h1Lines: [
      [{ t: "SHEETROCK COUNTS" }],
      [{ t: "WITHOUT ", accent: true }, { t: "THE" }],
      [{ t: "SHOWROOM RUN." }],
    ],
    intro:
      "How many sheets of sheetrock do I need? Measure the room once — walls and ceiling — and the sheetrock calculator returns the sheet count for 4×8 or 4×12 panels, plus the mud, tape and screws that finish the job.",
    faqs: [
      {
        q: "How many sheets of sheetrock do I need for a 10×12 room?",
        a: "With 8-foot ceilings, a 10×12 room has 352 sq ft of wall plus 120 sq ft of ceiling — 472 sq ft total. That is 15 sheets of 4×8, or about 16 with cutting waste. Switching to 4×12 sheets drops it to about 11.",
      },
      {
        q: "Is sheetrock the same thing as drywall?",
        a: "Yes. Sheetrock is a brand name (USG) that became the generic term, like Kleenex for tissue. Drywall, gypsum board, wallboard and sheetrock all describe the same panels — the sheet counts and math are identical.",
      },
      {
        q: "How much area does one sheet of sheetrock cover?",
        a: "A 4×8 sheet covers 32 square feet, a 4×10 covers 40 and a 4×12 covers 48. Longer sheets mean fewer butt joints to tape, which is why experienced hangers reach for 12-footers whenever the room allows them.",
      },
      {
        q: "How much waste should I add when ordering sheetrock?",
        a: "Add 10% for a simple rectangular room and up to 15% for rooms with many corners, soffits or angled ceilings. Standard door and window openings can stay in the wall area — their square footage roughly offsets the cut waste.",
      },
    ],
    relatedSlugs: ["drywall-calculator", "paint-calculator", "siding-calculator"],
    cardBlurb: "Sheetrock sheets per room with mud, tape and screws included.",
    ctaText: "Try the Sheetrock Calculator",
  },

  "shingle-calculator": {
    ...PAGES["roofing-calculator"],
    slug: "shingle-calculator",
    alias: true,
    primaryCta: { label: "Try the Shingle Calculator", href: `${APP}/?tab=roofing` },
    seoTitle: "Free Shingle Calculator — Bundles & Squares · No Login | MatCalc360",
    metaDescription:
      "Free shingle calculator — no login, no signup. Find out how many bundles of shingles you need: roof squares, bundle counts and underlayment with pitch and waste factored in.",
    kicker: "MC360 // Roofing & Exteriors",
    h1Lines: [
      [{ t: "HOW MANY BUNDLES?" }],
      [{ t: "COUNTED ", accent: true }, { t: "BEFORE" }],
      [{ t: "THE DELIVERY." }],
    ],
    intro:
      "How many bundles of shingles do I need? The answer lives in roof squares: measure the footprint, apply the pitch, add waste — the shingle calculator turns all three into a bundle count the supplier can load without a callback.",
    faqs: [
      {
        q: "How many bundles of shingles do I need for a 1,500 sq ft roof?",
        a: "A 1,500 sq ft footprint at a 6/12 pitch is about 1,677 sq ft of actual roof — 16.8 squares. At three bundles per square that is 50 bundles, or about 55 with 10% waste. The shingle calculator does the pitch adjustment for you.",
      },
      {
        q: "How many shingles are in a bundle?",
        a: "Typically 26 to 29 standard shingles per bundle, with three bundles covering one square (100 sq ft). The piece count varies by manufacturer — the coverage per bundle is the number that matters for ordering.",
      },
      {
        q: "Do 3-tab and architectural shingles need the same bundle count?",
        a: "Both standard 3-tab and architectural shingles run three bundles per square, so the count is the same. Heavyweight designer and luxury shingles often need four bundles per square — check your product's stated coverage.",
      },
      {
        q: "What if my roof is metal instead of shingles?",
        a: "The MatCalc360 roofing calculator has a dedicated metal mode that computes panel lengths and counts instead of bundles, plus 3-tab, architectural and cedar modes. Same measurements in — the right material list out.",
      },
    ],
    relatedSlugs: ["roofing-calculator", "siding-calculator", "brick-calculator"],
    cardBlurb: "Shingle bundles, squares and underlayment with pitch and waste included.",
    ctaText: "Try the Shingle Calculator",
  },

  "lumber-calculator": {
    ...PAGES["board-feet-calculator"],
    slug: "lumber-calculator",
    alias: true,
    primaryCta: { label: "Try the Lumber Calculator", href: `${APP}/?tab=boardfeet` },
    seoTitle: "Free Lumber Calculator — Board Feet & Piece Counts · No Login | MatCalc360",
    metaDescription:
      "Free lumber calculator — no login, no signup. Calculate board feet and piece counts for any lumber order: framing, decking and hardwood lists with pricing per thousand board feet.",
    kicker: "MC360 // Wood & Lumber",
    h1Lines: [
      [{ t: "THE LUMBER LIST," }],
      [{ t: "TOTALED " }, { t: "RIGHT.", accent: true }],
    ],
    intro:
      "How much lumber do I need? Whether the list is forty 2×4s or a stack of rough cherry, it all reduces to board feet. Enter dimensions and quantities — the lumber calculator totals the order and prices it per thousand.",
    faqs: [
      {
        q: "How do I calculate how much lumber I need?",
        a: "Break the project into pieces by size: count each length and cross-section separately, convert each group to board feet (thickness × width × length ÷ 12), then add waste. The lumber calculator totals mixed lists in one pass.",
      },
      {
        q: "How many board feet of lumber are in a wall?",
        a: "A quick framing rule: about one board foot of studs per square foot of wall at 16-inch spacing, plus plates and headers. A 100 sq ft wall runs 100–130 board feet of framing before sheathing enters the list.",
      },
      {
        q: "What is the formula for board feet of lumber?",
        a: "Thickness in inches × width in inches × length in feet ÷ 12. A 2×6 ten feet long is 10 board feet; a 1×8 twelve feet long is 8. The lumber calculator applies the formula to every line of your list.",
      },
      {
        q: "How is lumber priced — per piece or per board foot?",
        a: "Dimensional softwood (2×4s, 2×6s) sells per piece at retail yards. Hardwood and rough lumber sell per board foot, quoted per thousand (MBF). The calculator handles both so quotes compare cleanly.",
      },
    ],
    relatedSlugs: ["board-feet-calculator", "storage-building-calculator", "roofing-calculator"],
    cardBlurb: "Board feet and piece counts for framing, decking and hardwood lists.",
    ctaText: "Try the Lumber Calculator",
  },

  "shed-calculator": {
    ...PAGES["storage-building-calculator"],
    slug: "shed-calculator",
    alias: true,
    primaryCta: { label: "Try the Shed Calculator", href: `${APP}/?tab=materials` },
    seoTitle: "Free Shed Calculator — Material List & Cost Estimate · No Login | MatCalc360",
    metaDescription:
      "Free shed calculator — no login, no signup. Get a complete shed material list and cost estimate: framing studs, sheathing sheets, roofing squares and floor materials for any shed size.",
    kicker: "MC360 // Wood & Lumber",
    h1Lines: [
      [{ t: "BUILD THE SHED" }],
      [{ t: "WITH ", accent: true }, { t: "ONE LIST." }],
    ],
    intro:
      "What materials do I need to build a shed? Enter the footprint and wall height — the shed calculator returns the full material list: studs, sheathing, floor, roofing and an estimated cost, so build-vs-buy is decided with real numbers.",
    faqs: [
      {
        q: "What materials do I need for a 10×12 shed?",
        a: "A 10×12 shed with 8-foot walls takes roughly 52 wall studs, 14 sheets of wall sheathing, floor joists and decking for 120 sq ft, and about 1.5 squares of roofing. The shed calculator generates the exact line-item list.",
      },
      {
        q: "How much does it cost to build a 10×12 shed?",
        a: "DIY materials for a 10×12 shed typically run $1,800–$3,000 depending on siding and roofing choices — versus $4,000 or more for an equivalent prebuilt unit. The calculator's cost estimate uses adjustable unit prices for your market.",
      },
      {
        q: "How many studs do I need for a 10×12 shed?",
        a: "The 44 feet of wall perimeter needs about 44 studs at 16-inch spacing, plus corners, door framing and doubled top plates — call it 52 to 55 once the extras are counted honestly.",
      },
      {
        q: "What foundation does a shed need?",
        a: "Small sheds sit fine on compacted gravel pads or concrete blocks; larger or permanent buildings deserve a slab or pier foundation. Size the gravel pad with the stone calculator — a 10×12 pad at 4 inches takes about 2 tons.",
      },
    ],
    relatedSlugs: ["storage-building-calculator", "board-feet-calculator", "stone-calculator"],
    cardBlurb: "Full shed material list with framing, sheathing, roofing and cost.",
    ctaText: "Try the Shed Calculator",
  },
};

Object.assign(PAGES, ALIASES);

export const PAGE_LIST = Object.values(PAGES).filter((p) => !p.alias);

export const INDEX_PAGE = {
  slug: "",
  seoTitle: "Free Construction Calculators — Roofing, Concrete & More · No Login | MatCalc360",
  metaDescription:
    "Free construction calculators — no login, no signup. Roofing, concrete, asphalt, brick, stone, lumber, siding, paint, drywall, shed materials and a unit converter. Built for the jobsite.",
  kicker: "MC360 // Free Construction Calculators",
  h1Lines: [
    [{ t: "EVERY MATERIAL." }],
    [{ t: "EVERY PROJECT." }],
    [{ t: "ONE " }, { t: "CALCULATOR.", accent: true }],
  ],
  intro:
    "MatCalc360 turns field measurements into exact material orders — roofing, concrete, asphalt, brick, stone, lumber, siding, paint, drywall and more. Every tool is free with no login: open it and calculate.",
  heroImage: IMG("photo-1541888946425-d81bb19240f5"),
  heroImageAlt: "Construction cranes silhouetted against the sky at sunrise",
  primaryCta: { label: "Browse the Calculators", href: "#calculators" },
  secondaryCta: { label: "Open matcalc360.pro", href: "https://matcalc360.pro" },
  visualImage: IMG("photo-1503387762-592deb58ef4e"),
  visualImageAlt: "Architectural structure and building plans",
  visualStat: { kicker: "THE TOOLBOX", value: "15", label: "free calculators and tools inside MatCalc360 — no login, no paywall" },
};
